
import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLocalization } from '../hooks/useLocalization';
import { useCachedHotelData } from '../hooks/useCachedHotelData';
import { getProductInfo, searchTourvisio, searchWithCache, getOfferDetails, getGoogleHotelDetails, GoogleHotelDetails, GoogleRoom, searchRealFlights, RealFlightOption, getPagingData, mapFlightSegment, getApiSettings, BrandedFare } from '../services/tourvisioService';
import BrandedFareSelector from './hotel/BrandedFareSelector';
import { getHotelFromCMS, getHotelBySlug, saveHotelToCMS } from '../services/dataService';
import { translateContent, getStructuredHotelDescription, getAttractionDetails, getFlightInsight, FlightInsight } from '../services/aiService';
import { fetchBookingData, BookingData, findInstagramHandle, fetchHotelRooms } from '../services/apifyService';
import { getRandomImageForTerm } from '../services/imageService';
import { getImageUrl, getHeroImageUrl, getThumbnailUrl, stripImageParams } from '../utils/imageUtils';
import { SearchType } from '../types';
import type { Hotel, ProductInfo, ApiLogEntry, SearchQuery, Offer, StructuredHotelContent, FlightSegment, HolidayPackage, RoomScrapeData, TransportationOption, Language } from '../types';
import { LocalizationContext } from '../contexts/LocalizationContext';
import { CachedPricesSection } from './hotel/sections/CachedPricesSection';
import AlternativeDatesBar from './hotel/AlternativeDatesBar';
import { FlightDetailsCompact, formatDuration } from './hotel/FlightDetailsComponents';
import { CacheMetadata, CachedOffer } from '../types/hotelSectionTypes';
import TourDetailsModal from './hotel/TourDetailsModal';

// ==========================================
// HELPER: Get airline name as string
// ==========================================
const getAirlineName = (airlineData: any): string => {
    if (!airlineData) return 'Charter';
    if (typeof airlineData === 'string') return airlineData;
    if (typeof airlineData === 'object' && airlineData.name) {
        return typeof airlineData.name === 'string' ? airlineData.name : 'Charter';
    }
    return 'Charter';
};

// ==========================================
// HELPER: Format flight date/time for display
// ==========================================
const formatFlightDateTime = (dateTimeStr: any, locale: string = 'sv-SE'): { date: string; time: string } => {
    if (!dateTimeStr) return { date: '', time: '' };

    // Defensive: coerce to string to prevent .match() crash on non-string values
    const str = String(dateTimeStr);

    // Handle ISO format: "2026-03-06T13:55:00"
    const d = new Date(str);
    if (isNaN(d.getTime())) {
        // If not valid date, try to extract time from string like "HH:mm"
        const match = str.match(/(\d{1,2}:\d{2})/);
        return { date: '', time: match ? match[1] : str };
    }

    const datePart = d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
    const timePart = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false });

    return { date: datePart, time: timePart };
};

// ==========================================
// COMPONENT: SanFlight Selection
// ==========================================
const SanFlightSelection: React.FC<{
    outboundFlights: RealFlightOption[];
    inboundFlights: RealFlightOption[];
    selectedOutbound: RealFlightOption | null;
    selectedInbound: RealFlightOption | null;
    onSelectOutbound: (flight: RealFlightOption) => void;
    onSelectInbound: (flight: RealFlightOption) => void;
    language: Language;
    currency: string;
}> = ({ outboundFlights, inboundFlights, selectedOutbound, selectedInbound, onSelectOutbound, onSelectInbound, language, currency }) => {
    // Ensure arrays are never undefined to prevent map errors
    const safeOutbound = outboundFlights || [];
    const safeInbound = inboundFlights || [];

    if (safeOutbound.length === 0 && safeInbound.length === 0) return null;

    const renderFlightCard = (flight: RealFlightOption, isSelected: boolean, onClick: () => void, label: string) => {
        const priceDiff = flight.priceDifference;
        const currencySymbol = currency === 'SEK' || currency === 'DKK' ? 'kr' : '€';
        const formattedPrice = Math.round(priceDiff).toLocaleString(language === 'sv' ? 'sv-SE' : language === 'da' ? 'da-DK' : 'en-US');
        let priceText = '';
        if (priceDiff > 0) {
            priceText = currency === 'SEK' || currency === 'DKK' ? `+${formattedPrice} kr` : `+${formattedPrice} €`;
        } else {
            priceText = isSelected 
                ? (language === 'sv' ? 'Ingår' : language === 'da' ? 'Inkluderet' : 'Included') 
                : (currency === 'SEK' || currency === 'DKK' ? '0 kr' : '0 €');
        }

        return (
            <div
                onClick={onClick}
                className={`
                    relative p-3 rounded-xl border-2 cursor-pointer transition-all duration-200
                    ${isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-100 hover:border-blue-200 bg-white dark:bg-gray-800 dark:border-gray-700'
                    }
                `}
            >
                {/* Selection Circle */}
                <div className={`absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center
                    ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}
                `}>
                    {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                </div>

                <div className="flex items-center gap-3 mb-2">
                    {flight.logoUrl ? (
                        <img src={flight.logoUrl} alt={flight.airline} className="h-6 w-auto object-contain" onError={(e) => e.currentTarget.style.display = 'none'} />
                    ) : (
                        <span className="text-xs font-bold text-gray-500">{flight.airline}</span>
                    )}
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</div>
                </div>

                <div className="flex justify-between items-end">
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-gray-900 dark:text-white">{flight.departureTime}</span>
                            <span className="text-gray-400 text-xs">→</span>
                            <span className="text-lg font-bold text-gray-900 dark:text-white">{flight.arrivalTime}</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {flight.departureAirport} - {flight.arrivalAirport} • {flight.duration}
                            {flight.stops > 0 && <span className="text-red-500 ml-1">• {flight.stops} Stop</span>}
                        </div>
                    </div>

                    <div className={`text-sm font-bold ${priceDiff > 0 ? 'text-orange-500' : 'text-green-600'}`}>
                        {priceText}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 mb-8">
            <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight mb-6 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                {language === 'sv' ? 'Betala och boka' : 'Pay and Book'}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Outbound Column */}
                <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                        {language === 'sv' ? 'Utresa' : 'Outbound'} • {selectedOutbound?.departureDate}
                    </h4>
                    <div className="space-y-3 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                        {safeOutbound.map((flight) => (
                            renderFlightCard(
                                flight,
                                selectedOutbound?.id === flight.id,
                                () => onSelectOutbound(flight),
                                flight.airline
                            )
                        ))}
                    </div>
                </div>

                {/* Inbound Column */}
                <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                        {language === 'sv' ? 'Hemresa' : 'Return'}
                    </h4>
                    <div className="space-y-3 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                        {safeInbound.map((flight) => (
                            renderFlightCard(
                                flight,
                                selectedInbound?.id === flight.id,
                                () => onSelectInbound(flight),
                                flight.airline
                            )
                        ))}
                    </div>
                </div>
            </div>

            {(selectedOutbound?.priceDifference || 0) + (selectedInbound?.priceDifference || 0) > 0 && (
                <div className="mt-6 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg flex items-center justify-between">
                    <span className="text-sm font-medium text-orange-800 dark:text-orange-300">
                        {language === 'sv' ? 'Extra kostnad för valda flyg:' : 'Extra cost for selected flights:'}
                    </span>
                    <span className="text-lg font-bold text-orange-600 dark:text-orange-400">
                        {(() => {
                            const diff = (selectedOutbound?.priceDifference || 0) + (selectedInbound?.priceDifference || 0);
                            const formatted = Math.round(diff).toLocaleString(language === 'sv' ? 'sv-SE' : language === 'da' ? 'da-DK' : 'en-US');
                            return currency === 'SEK' || currency === 'DKK' ? `+ ${formatted} kr` : `+ ${formatted} €`;
                        })()}
                    </span>
                </div>
            )}
        </div>
    );
};

interface HotelDetailProps {
    logApiCall: (log: ApiLogEntry) => void;
    embeddedHotel?: Hotel;
    embeddedSearchQuery?: SearchQuery;
    onClose?: () => void;
}

// --- Icons ---
const DiningIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>;
const PoolIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" /></svg>;
const RoomIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>;
const GeneralIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const LocationIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>;
const FamilyIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>;
const WellnessIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 21a9 9 0 0 0 9-9c0-4.97-4.03-9-9-9s-9 4.03-9 9 0 4.03 9 9Z" /><path d="M8 12s1.5-2 4-2 4 2 4 2" /><path d="M9 16.5s1.5-1.5 3-1.5 3 1.5 3 1.5" /></svg>;

// Fallback Icons for Attractions
const MuseumIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" /></svg>;
const TreeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>;
const ShoppingBagIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>;
const TicketIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>;

// Specific Facility Icons based on the user screenshot map
const getFacilityIcon = (facility: string): React.ReactNode => {
    const f = (facility || '').toLowerCase();

    // Food & Drink (Orange)
    if (f.includes('restaurant') || f.includes('breakfast') || f.includes('buffet') || f.includes('dining'))
        return <svg className="h-5 w-5 text-orange-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>;
    if (f.includes('bar') || f.includes('pub') || f.includes('drink') || f.includes('mini bar'))
        return <svg className="h-5 w-5 text-orange-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z" /></svg>;
    if (f.includes('snack') || f.includes('cafe'))
        return <svg className="h-5 w-5 text-orange-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>;

    // Water & Pool (Blue)
    if (f.includes('pool') || f.includes('swim'))
        return <svg className="h-5 w-5 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" /></svg>;
    if (f.includes('beach') || f.includes('sea'))
        return <svg className="h-5 w-5 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" /></svg>;
    if (f.includes('slide') || f.includes('aquapark'))
        return <svg className="h-5 w-5 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg>;

    // Wellness (Pink/Purple)
    if (f.includes('spa') || f.includes('massage') || f.includes('sauna') || f.includes('hamam') || f.includes('bath') || f.includes('beauty'))
        return <svg className="h-5 w-5 text-pink-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 21a9 9 0 0 0 9-9c0-4.97-4.03-9-9-9s-9 4.03-9 9 0 4.03 9 9Z" /><path d="M8 12s1.5-2 4-2 4 2 4 2" /><path d="M9 16.5s1.5-1.5 3-1.5 3 1.5 3 1.5" /></svg>;

    // Sports & Fitness (Red)
    if (f.includes('gym') || f.includes('fitness') || f.includes('sport') || f.includes('tennis'))
        return <svg className="h-5 w-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>;

    // Kids (Green)
    if (f.includes('kid') || f.includes('child') || f.includes('playground'))
        return <svg className="h-5 w-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;

    // Services & General (Teal/Gray)
    if (f.includes('wifi') || f.includes('internet'))
        return <svg className="h-5 w-5 text-indigo-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" /></svg>;
    if (f.includes('park'))
        return <svg className="h-5 w-5 text-teal-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>;
    if (f.includes('service') || f.includes('reception') || f.includes('laundry'))
        return <svg className="h-5 w-5 text-teal-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
    if (f.includes('air') || f.includes('condition'))
        return <svg className="h-5 w-5 text-teal-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" /></svg>;

    return <svg className="h-5 w-5 text-teal-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>;
};

const StarIcon: React.FC<{ filled: boolean, size?: string }> = ({ filled, size = "w-6 h-6" }) => (
    <svg className={`${size} ${filled ? 'text-amber-400' : 'text-gray-300 dark:text-gray-600'}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
);

// Updated Weather Widget
const WeatherWidget: React.FC<{ lat: string; lon: string; checkIn: string; language: string }> = ({ lat, lon, checkIn, language }) => {
    const [weather, setWeather] = useState<any>(null);
    const [seaTemp, setSeaTemp] = useState<number | null>(null);

    useEffect(() => {
        const fetchWeather = async () => {
            try {
                const isSummer = new Date(checkIn).getMonth() >= 4 && new Date(checkIn).getMonth() <= 8;
                setWeather({
                    tempMax: isSummer ? 33 : 22,
                    tempMin: isSummer ? 24 : 15,
                    code: 0,
                    isForecast: true
                });
                setSeaTemp(isSummer ? 26 : 19);
            } catch (e) { console.error(e); }
        };
        fetchWeather();
    }, [lat, lon, checkIn]);

    if (!weather) return null;

    return (
        <div className="w-full bg-gradient-to-r from-[#3b82f6] to-[#0ea5e9] dark:from-blue-900 dark:to-blue-800 rounded-2xl p-4 text-white shadow-lg mb-8 relative overflow-hidden flex flex-col md:flex-row items-center justify-between">
            <div className="absolute -right-10 -top-10 opacity-20">
                <svg className="w-48 h-48 text-yellow-300" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.59-1.591a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" /></svg>
            </div>
            <div className="z-10 flex items-center gap-3 mb-3 md:mb-0">
                <div className="p-2 bg-white/20 rounded-full backdrop-blur-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <div>
                    <h4 className="font-bold text-sm uppercase tracking-wide opacity-90">{language === 'sv' ? 'VÄDER' : 'WEATHER'}</h4>
                    <p className="text-xs opacity-75 font-medium">{new Date(checkIn).toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                </div>
            </div>
            <div className="flex items-center gap-6 z-10">
                <div className="text-center">
                    <div className="flex items-center gap-1 justify-center">
                        <svg className="w-6 h-6 text-yellow-300 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                        <span className="text-3xl font-black">{Math.round(weather.tempMax)}°</span>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider opacity-80 mt-1 block">{language === 'sv' ? 'LUFT' : 'AIR TEMP'}</span>
                </div>
                <div className="w-px h-10 bg-white/30 hidden md:block"></div>
                <div className="text-center">
                    <div className="flex items-center gap-1 justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" /></svg>
                        <span className="text-3xl font-black">{seaTemp ? Math.round(seaTemp) : '--'}°</span>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider opacity-80 mt-1 block">{language === 'sv' ? 'HAV' : 'SEA'}</span>
                </div>
            </div>
        </div>
    );

};

const parseDescriptionLocally = (text: string, lang: string = 'en'): StructuredHotelContent => {
    const result: StructuredHotelContent = {
        intro: '',
        dining: '', activities: '', rooms: '', location: '', family: '', wellness: '', other: ''
    };

    if (!text) return result;

    const diningKeys = ['dining', 'restaurant', 'mat', 'food', 'frukost', 'breakfast', 'lunch', 'middag', 'dinner', 'all inclusive', 'måltid'];
    const activityKeys = ['pool', 'activit', 'pooler', 'sport', 'gym', 'fitness', 'träning', 'underhållning', 'entertainment'];
    const roomKeys = ['room', 'accommodation', 'rum', 'boende', 'sviter', 'suites', 'balkong', 'balcony'];
    const locationKeys = ['location', 'situated', 'läge', 'avstånd', 'distance', 'beläget', 'center', 'centrum', 'flygplats', 'airport'];
    const familyKeys = ['child', 'family', 'barn', 'familj', 'miniklubb', 'lekplats', 'playground', 'vattenpark', 'aquapark'];
    const wellnessKeys = ['spa', 'wellness', 'bastu', 'sauna', 'massage', 'turkish bath', 'hamam', 'skönhet', 'beauty'];

    const paragraphs = text.split(/\n\n|(?:\n(?=[A-ZÖÄÅ].{0,30}\n))/); // Split by double newline or potential headers

    let currentParagraphs = 0;
    paragraphs.forEach(p => {
        const trimmed = p.trim();
        if (!trimmed) return;
        const pLower = trimmed.toLowerCase();

        if (currentParagraphs < 1 && trimmed.length > 50) {
            result.intro = trimmed;
            currentParagraphs++;
            return;
        }

        if (diningKeys.some(k => pLower.includes(k))) { result.dining += trimmed + '\n\n'; }
        else if (activityKeys.some(k => pLower.includes(k))) { result.activities += trimmed + '\n\n'; }
        else if (roomKeys.some(k => pLower.includes(k))) { result.rooms += trimmed + '\n\n'; }
        else if (locationKeys.some(k => pLower.includes(k))) { result.location += trimmed + '\n\n'; }
        else if (familyKeys.some(k => pLower.includes(k))) { result.family += trimmed + '\n\n'; }
        else if (wellnessKeys.some(k => pLower.includes(k))) { result.wellness += trimmed + '\n\n'; }
        else {
            result.other += trimmed + '\n\n';
        }
        currentParagraphs++;
    });

    if (!result.intro && text.length > 0) result.intro = text.substring(0, Math.min(text.length, 300));

    return result;
};

const SectionWidget: React.FC<{ title: string; icon: React.ReactNode; content: string; colorClass: string; textColorClass: string; darkColorClass?: string }> = ({ title, icon, content, colorClass, textColorClass, darkColorClass }) => {
    if (!content) return null;

    // Strip HTML tags and decode entities for clean display
    const cleanContent = (html: string): string => {
        // First, replace line-break tags with actual newlines
        let text = html.replace(/<br\s*\/?>/gi, '\n');
        // Replace paragraph tags with double newlines
        text = text.replace(/<\/p>\s*<p>/gi, '\n\n');
        text = text.replace(/<p[^>]*>/gi, '');
        text = text.replace(/<\/p>/gi, '\n');
        // Strip all other HTML tags
        text = text.replace(/<[^>]+>/g, '');
        // Decode common HTML entities
        text = text.replace(/&nbsp;/gi, ' ');
        text = text.replace(/&amp;/gi, '&');
        text = text.replace(/&lt;/gi, '<');
        text = text.replace(/&gt;/gi, '>');
        text = text.replace(/&quot;/gi, '"');
        text = text.replace(/&#39;/gi, "'");
        // Clean up extra whitespace
        text = text.replace(/\n{3,}/g, '\n\n');
        return text.trim();
    };

    const displayContent = cleanContent(content);

    return (
        <div className={`rounded-2xl p-6 border-2 shadow-sm transition-all hover:shadow-md ${colorClass} ${darkColorClass || 'dark:bg-gray-800 dark:border-gray-700'}`}>
            <div className="flex flex-col sm:flex-row items-start gap-4">
                <div className={`p-3 rounded-2xl bg-white dark:bg-gray-800 shadow-sm border border-black/5 ${textColorClass} dark:text-gray-200`}>
                    <div className="w-6 h-6">{icon}</div>
                </div>
                <div className="flex-1">
                    <h3 className={`font-black text-xs uppercase tracking-widest mb-3 opacity-80 ${textColorClass} dark:text-white`}>{title}</h3>
                    <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-200 whitespace-pre-line font-medium">{displayContent}</p>
                </div>
            </div>
        </div>
    );
};

// -- Booking.com Style Review Summary Card --
const ReviewSummaryCard: React.FC<{ bookingData: BookingData | null }> = ({ bookingData }) => {
    // Default fallback values if no data
    const score = bookingData?.score || 9.4;
    const wordRating = bookingData?.wordRating || "Superb";
    const count = bookingData?.reviewCount || 1245;
    const subScores = bookingData?.subScores || {
        staff: 9.7,
        comfort: 9.5,
        cleanliness: 9.4,
        location: 9.8
    };
    const highlights = bookingData?.highlights || ["Private Beach", "Exceptional Staff", "Stunning Views", "Infinity Pool"];

    const ProgressBar = ({ label, value }: { label: string, value: number }) => (
        <div className="mb-2">
            <div className="flex justify-between text-[10px] font-bold text-white mb-1 uppercase tracking-wide">
                <span>{label}</span>
                <span>{value.toFixed(1)}</span>
            </div>
            <div className="w-full bg-blue-800 dark:bg-blue-900 rounded-full h-1.5">
                <div
                    className="bg-yellow-400 h-1.5 rounded-full"
                    style={{ width: `${(value / 10) * 100}%` }}
                ></div>
            </div>
        </div>
    );

    // Slimmer, lighter version
    return (
        <div className="bg-[#1e3a8a] dark:bg-blue-900 rounded-xl p-5 text-white shadow-lg mb-4">
            <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">

                {/* Left: Overall Score */}
                <div className="flex items-center gap-3 md:w-1/4">
                    <div className="bg-white text-[#1e3a8a] dark:text-blue-900 w-16 h-16 rounded-xl flex items-center justify-center text-3xl font-black shadow-lg">
                        {score.toFixed(1)}
                    </div>
                    <div>
                        <h3 className="text-xl font-bold leading-none mb-1">{wordRating}</h3>
                        <div className="inline-block bg-blue-800/50 dark:bg-blue-800 px-1.5 py-0.5 rounded text-[9px] text-blue-200 border border-blue-700 mb-0.5">
                            Booking.com Verified
                        </div>
                        <p className="text-[10px] text-blue-200">{count.toLocaleString()} reviews</p>
                    </div>
                </div>

                {/* Middle: Vertical Separator & Progress Bars */}
                <div className="md:w-1/2 w-full border-t md:border-t-0 md:border-l border-blue-800 dark:border-blue-800 pt-4 md:pt-0 md:pl-6">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                        <ProgressBar label="Staff" value={subScores.staff} />
                        <ProgressBar label="Comfort" value={subScores.comfort} />
                        <ProgressBar label="Cleanliness" value={subScores.cleanliness} />
                        <ProgressBar label="Location" value={subScores.location} />
                    </div>
                </div>

                {/* Right: Highlights */}
                <div className="md:w-1/4 w-full border-t md:border-t-0 md:border-l border-blue-800 dark:border-blue-800 pt-4 md:pt-0 md:pl-6">
                    <h4 className="text-[10px] font-bold text-blue-300 uppercase tracking-widest mb-2">Highlights</h4>
                    <div className="flex flex-wrap gap-1.5">
                        {highlights.slice(0, 4).map((tag, idx) => (
                            <span key={idx} className="bg-blue-800/60 dark:bg-blue-800 border border-blue-600/50 rounded px-2 py-1 text-[10px] font-medium flex items-center gap-1 transition-colors cursor-default">
                                <svg className="w-2.5 h-2.5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const ReviewTicker: React.FC = () => {
    const reviews = [
        { text: "Absolutely loved the pool area! The water was pristine.", author: "Anna S." },
        { text: "Staff went above and beyond to make our anniversary special.", author: "John D." },
        { text: "Food was diverse and delicious. The breakfast buffet had everything.", author: "Sarah M." },
        { text: "Room view was breathtaking. Watching the sunset was a highlight.", author: "Mike R." },
        { text: "Perfect location near the beach. Just a short walk to the sand.", author: "Emma W." },
        { text: "Kids club was amazing for our little ones. They didn't want to leave!", author: "David B." },
        { text: "Spa treatments were top notch. The massage was incredibly relaxing.", author: "Lisa K." },
        { text: "Very clean and hygienic rooms. Housekeeping did a fantastic job.", author: "Tom H." },
        { text: "Entertainment team was so much fun! Great shows every night.", author: "Jessica P." },
        { text: "Great value for money considering the quality of food and amenities.", author: "Robert L." }
    ];

    // Duplicate list for seamless loop
    const allReviews = [...reviews, ...reviews];

    return (
        <div className="relative h-28 overflow-hidden bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-2">
            <style>{`
                @keyframes vertical-scroll {
                    0% { transform: translateY(0); }
                    100% { transform: translateY(-50%); }
                }
                .animate-vertical-scroll {
                    animation: vertical-scroll 40s linear infinite;
                }
                .animate-vertical-scroll:hover {
                    animation-play-state: paused;
                }
            `}</style>
            <div className="absolute top-0 left-0 w-full h-6 bg-gradient-to-b from-gray-50 dark:from-gray-800 to-transparent z-10 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-full h-6 bg-gradient-to-t from-gray-50 dark:from-gray-800 to-transparent z-10 pointer-events-none"></div>

            <div className="animate-vertical-scroll">
                {allReviews.map((rev, i) => (
                    <div key={i} className="mb-2 bg-white dark:bg-gray-700 p-2 rounded shadow-sm border border-gray-100 dark:border-gray-600 mx-1">
                        <p className="text-[10px] text-gray-600 dark:text-gray-300 italic leading-snug">"{rev.text}"</p>
                        <p className="text-[9px] font-bold text-gray-900 dark:text-white mt-0.5 text-right">- {rev.author}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Swedish translations for common Tourvisio facility names
const FACILITY_TRANSLATIONS_SV: Record<string, string> = {
    'restaurant': 'Restaurang', 'bar': 'Bar', 'pool': 'Pool', 'swimming pool': 'Simbassäng',
    'outdoor pool': 'Utomhuspool', 'indoor pool': 'Inomhuspool', 'children pool': 'Barnpool',
    'kids pool': 'Barnpool', 'spa': 'Spa', 'sauna': 'Bastu', 'turkish bath': 'Turkiskt bad',
    'hamam': 'Hamam', 'massage': 'Massage', 'fitness': 'Gym', 'fitness center': 'Gym',
    'gym': 'Gym', 'wifi': 'Wi-Fi', 'free wifi': 'Gratis Wi-Fi', 'internet': 'Internet',
    'beach': 'Strand', 'private beach': 'Privat strand', 'sandy beach': 'Sandstrand',
    'air conditioning': 'Luftkonditionering', 'room service': 'Rumsservice',
    'parking': 'Parkering', 'free parking': 'Gratis parkering', 'valet parking': 'Betjänad parkering',
    'reception': 'Reception', '24-hour reception': 'Dygnet runt-reception',
    'laundry': 'Tvättservice', 'laundry service': 'Tvättservice',
    'playground': 'Lekplats', 'kids club': 'Barnklubb', "children's club": 'Barnklubb',
    'mini club': 'Miniklubb', 'baby sitting': 'Barnvakt', 'babysitting': 'Barnvakt',
    'tennis': 'Tennis', 'tennis court': 'Tennisbana', 'basketball': 'Basket',
    'football': 'Fotboll', 'volleyball': 'Volleyboll', 'table tennis': 'Bordtennis',
    'billiards': 'Biljard', 'darts': 'Dart', 'water sports': 'Vattensport',
    'diving': 'Dykning', 'snorkeling': 'Snorkling', 'windsurfing': 'Vindsurfing',
    'buffet': 'Buffé', 'a la carte': 'À la carte', 'snack bar': 'Snackbar',
    'cafe': 'Kafé', 'lobby bar': 'Lobbybar', 'pool bar': 'Poolbar', 'beach bar': 'Strandbar',
    'mini bar': 'Minibar', 'all inclusive': 'All inclusive',
    'animation': 'Underhållning', 'entertainment': 'Underhållning', 'live music': 'Livemusik',
    'disco': 'Diskoteket', 'nightclub': 'Nattklubb',
    'elevator': 'Hiss', 'lift': 'Hiss', 'wheelchair accessible': 'Rullstolsanpassat',
    'garden': 'Trädgård', 'terrace': 'Terrass', 'balcony': 'Balkong',
    'sea view': 'Havsutsikt', 'mountain view': 'Bergsutsikt',
    'aquapark': 'Vattenpark', 'water slide': 'Vattenrutschkana', 'water slides': 'Vattenrutschkanor',
    'conference room': 'Konferensrum', 'meeting room': 'Mötesrum',
    'currency exchange': 'Valutaväxling', 'atm': 'Bankomat',
    'doctor': 'Läkare', 'medical service': 'Sjukvård',
    'beauty salon': 'Skönhetssalong', 'hair salon': 'Frisör', 'hairdresser': 'Frisör',
    'shop': 'Butik', 'shopping': 'Shopping', 'gift shop': 'Souvenirbutik',
    'breakfast': 'Frukost', 'breakfast buffet': 'Frukostbuffé',
    'safe': 'Kassaskåp', 'safe box': 'Kassaskåp',
    'transfer': 'Transfer', 'airport transfer': 'Flygplatstransfer', 'shuttle': 'Skyttelbuss',
};

const HotelAmenities: React.FC<{ facilities: string[], language: string }> = ({ facilities, language }) => {
    if (!facilities || facilities.length === 0) return null;

    const translateFacility = (facility: string): string => {
        if (language !== 'sv') return facility;
        return FACILITY_TRANSLATIONS_SV[(facility || '').toLowerCase()] || facility;
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-sm border border-gray-100 dark:border-gray-700 mb-8">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 uppercase tracking-wide">
                {language === 'sv' ? 'Faciliteter' : 'Amenities'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {facilities.map((facility, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300 font-medium p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <div className="text-gray-400 dark:text-gray-500">{getFacilityIcon(facility)}</div>
                        <span className="truncate" title={translateFacility(facility)}>{translateFacility(facility)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// -- Enhanced Nearby Attractions with Specific Data & AI & Directions --
const NearbyAttractions: React.FC<{ city?: string, language: string }> = ({ city = '', language }) => {
    const [activeCategory, setActiveCategory] = useState<'culture' | 'nature' | 'shopping'>('culture');
    const [attractions, setAttractions] = useState<{ category: string, name: string, desc: string, icon: any }[]>([]);

    // City-Specific Data Mapping with Categories - EXPANDED with Gemini Knowledge
    const CITY_ATTRACTIONS: Record<string, { category: string, name: string, desc: string, icon: any }[]> = {
        "bodrum": [
            { category: 'culture', name: "Bodrum Castle", desc: "A magnificent medieval fortress built by the Knights Hospitaller.", icon: MuseumIcon },
            { category: 'culture', name: "Mausoleum at Halicarnassus", desc: "The ruins of one of the Seven Wonders of the Ancient World.", icon: MuseumIcon },
            { category: 'culture', name: "Bodrum Amphitheatre", desc: "Ancient theatre with stunning views over Bodrum harbour.", icon: MuseumIcon },
            { category: 'culture', name: "Myndos Gate", desc: "Historical gate that faced Alexander the Great.", icon: MuseumIcon },
            { category: 'culture', name: "Zeki Muren Arts Museum", desc: "House museum of the famous Turkish singer.", icon: MuseumIcon },
            { category: 'shopping', name: "Bodrum Marina", desc: "A lively area perfect for evening strolls and luxury yacht watching.", icon: ShoppingBagIcon },
            { category: 'shopping', name: "Bodrum Bazaar", desc: "Traditional market for textiles, spices and souvenirs.", icon: ShoppingBagIcon },
            { category: 'shopping', name: "Oasis Mall", desc: "Open-air shopping center with local and international brands.", icon: ShoppingBagIcon },
            { category: 'nature', name: "Camel Beach", desc: "A serene sandy beach with crystal clear waters.", icon: TreeIcon },
            { category: 'nature', name: "Bardakci Cove", desc: "Secluded cove with crystal clear water near the center.", icon: TreeIcon },
            { category: 'nature', name: "Windmills of Bodrum", desc: "Iconic stone windmills on the hill between Bodrum and Gumbet.", icon: TreeIcon },
            { category: 'nature', name: "Rabbit Island", desc: "Small island near Gumusluk reachable by walking through shallow water.", icon: TreeIcon }
        ],
        "antalya": [
            { category: 'culture', name: "Kaleici (Old Town)", desc: "Historic center with Roman ruins and Ottoman houses.", icon: MuseumIcon },
            { category: 'culture', name: "Hadrian's Gate", desc: "Beautifully preserved Roman triumphal arch.", icon: MuseumIcon },
            { category: 'culture', name: "Antalya Museum", desc: "One of Turkey's largest museums with Lycian artifacts.", icon: MuseumIcon },
            { category: 'culture', name: "Yivli Minare Mosque", desc: "Iconic fluted minaret, a symbol of the city.", icon: MuseumIcon },
            { category: 'culture', name: "Aspendos Theatre", desc: "Best-preserved Roman theatre in the world (nearby).", icon: MuseumIcon },
            { category: 'culture', name: "Perge Ancient City", desc: "Extensive ruins of a major Pamphylian city.", icon: MuseumIcon },
            { category: 'nature', name: "Duden Waterfalls", desc: "Spectacular waterfalls cascading into the Mediterranean.", icon: TreeIcon },
            { category: 'nature', name: "Konyaalti Beach", desc: "Famous stretch of pebble beach with stunning mountain views.", icon: TreeIcon },
            { category: 'nature', name: "Kursunlu Waterfall", desc: "Hidden nature park with pine forests and cascading water.", icon: TreeIcon },
            { category: 'nature', name: "Karaalioglu Park", desc: "Large park with miradors overlooking the sea and mountains.", icon: TreeIcon },
            { category: 'nature', name: "Antalya Aquarium", desc: "World's biggest tunnel aquarium.", icon: TreeIcon },
            { category: 'shopping', name: "MarkAntalya", desc: "Modern shopping mall in the city center.", icon: ShoppingBagIcon },
            { category: 'shopping', name: "TerraCity", desc: "Upscale shopping mall in the Lara district.", icon: ShoppingBagIcon },
            { category: 'shopping', name: "Old Bazaar", desc: "Traditional market streets for leather, gold, and spices.", icon: ShoppingBagIcon }
        ],
        "istanbul": [
            { category: 'culture', name: "Hagia Sophia", desc: "Iconic Byzantine basilica and Ottoman mosque.", icon: MuseumIcon },
            { category: 'culture', name: "Blue Mosque", desc: "Historic mosque known for its blue tiles.", icon: MuseumIcon },
            { category: 'culture', name: "Topkapi Palace", desc: "Opulent palace of the Ottoman sultans.", icon: MuseumIcon },
            { category: 'culture', name: "Basilica Cistern", desc: "Ancient underground water reservoir with Medusa heads.", icon: MuseumIcon },
            { category: 'culture', name: "Galata Tower", desc: "Medieval stone tower offering panoramic views of Istanbul.", icon: MuseumIcon },
            { category: 'culture', name: "Dolmabahce Palace", desc: "Grand 19th-century palace on the Bosphorus.", icon: MuseumIcon },
            { category: 'shopping', name: "Grand Bazaar", desc: "One of the largest and oldest covered markets in the world.", icon: ShoppingBagIcon },
            { category: 'shopping', name: "Spice Bazaar", desc: "Colorful market for spices, sweets, and dried fruits.", icon: ShoppingBagIcon },
            { category: 'shopping', name: "Istiklal Avenue", desc: "Famous pedestrian street with shops, cafes, and historic tram.", icon: ShoppingBagIcon },
            { category: 'nature', name: "Gulhane Park", desc: "Historic urban park adjacent to Topkapi Palace.", icon: TreeIcon },
            { category: 'nature', name: "Bosphorus Strait", desc: "Scenic strait separating Europe and Asia.", icon: TreeIcon },
            { category: 'nature', name: "Pierre Loti Hill", desc: "Hilltop offering sweeping views of the Golden Horn.", icon: TreeIcon }
        ],
        // Default / Fallback
        "default": [
            { category: 'shopping', name: "City Center", desc: "Explore the vibrant heart of the city with shops and cafes.", icon: ShoppingBagIcon },
            { category: 'culture', name: "Local Museum", desc: "Discover the history and culture of the region.", icon: MuseumIcon },
            { category: 'nature', name: "Nature Park", desc: "Relax in beautiful green spaces nearby.", icon: TreeIcon }
        ]
    };

    useEffect(() => {
        const normalizedCity = (city || '').toLowerCase().trim();
        const items = CITY_ATTRACTIONS[normalizedCity] || CITY_ATTRACTIONS["default"];
        setAttractions(items);
    }, [city]);

    const filteredAttractions = attractions.filter(a => a.category === activeCategory);

    const getDirectionsUrl = (name: string) => {
        return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(name + ' ' + (city || ''))}`;
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700">
                <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <LocationIcon />
                    {language === 'sv' ? 'I Närheten' : 'Nearby Attractions'}
                </h3>

                {/* Category Tabs */}
                <div className="flex space-x-2">
                    {[
                        { id: 'culture', label: language === 'sv' ? 'Kultur & Historia' : 'Culture' },
                        { id: 'nature', label: language === 'sv' ? 'Natur & Park' : 'Nature' },
                        { id: 'shopping', label: language === 'sv' ? 'Shopping & City' : 'Shopping' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveCategory(tab.id as any)}
                            className={`px-3 py-1.5 text-xs font-bold rounded-full transition-all ${activeCategory === tab.id ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="p-6 space-y-6">
                {filteredAttractions.map((attr, idx) => (
                    <div key={idx} className="flex gap-4 items-start group">
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-xl text-gray-400 dark:text-gray-500 group-hover:bg-amber-50 dark:group-hover:bg-amber-900/30 group-hover:text-amber-500 dark:group-hover:text-amber-400 transition-colors">
                            <attr.icon />
                        </div>
                        <div className="flex-1">
                            <div className="flex justify-between items-start">
                                <h4 className="font-bold text-gray-900 dark:text-white text-sm group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">{attr.name}</h4>
                                <a
                                    href={getDirectionsUrl(attr.name)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
                                >
                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
                                    {language === 'sv' ? 'Vägbeskrivning' : 'Directions'}
                                </a>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mt-1">{attr.desc}</p>
                        </div>
                    </div>
                ))}
                {filteredAttractions.length === 0 && (
                    <p className="text-center text-xs text-gray-400 dark:text-gray-500 italic py-4">No attractions found in this category.</p>
                )}
            </div>
        </div>
    );
};

const SidebarGallery: React.FC<{ images: string[], onClick: (index: number) => void }> = ({ images, onClick }) => {
    if (!images || images.length === 0) return null;

    // Display up to 6 images (3x2 grid)
    const displayImages = images.slice(0, 6);
    const remaining = images.length - 6;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-3 shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3">Gallery</h3>
            <div className="grid grid-cols-3 gap-1">
                {displayImages.map((img, idx) => (
                    <div
                        key={idx}
                        className="relative aspect-square rounded-md overflow-hidden cursor-pointer group"
                        onClick={() => onClick(idx)}
                    >
                        <img src={img} alt={`Gallery ${idx}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />

                        {/* Overlay for last image if more exist */}
                        {idx === 5 && remaining > 0 && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-[1px] transition-colors hover:bg-black/70">
                                <span className="text-white font-bold text-xs">+{remaining}</span>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <button
                onClick={() => onClick(0)}
                className="w-full mt-3 py-2 text-[10px] font-bold text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors uppercase tracking-wide"
            >
                View All Photos
            </button>
        </div>
    );
};

const StickyBookingBar: React.FC<{
    hotel: Hotel | HolidayPackage;
    lowestPrice: number;
    currency: string;
    language: string;
    searchQuery?: SearchQuery;
    onBook: () => void;
    visible: boolean;
    selectedOutbound?: RealFlightOption | null;
    selectedInbound?: RealFlightOption | null;
}> = ({ hotel, lowestPrice, currency, language, searchQuery, onBook, visible, selectedOutbound, selectedInbound }) => {
    if (!visible) return null;

    // Helper to format dates for display
    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US', { day: 'numeric', month: 'short' });
    };

    const checkIn = formatDate(searchQuery?.checkIn);
    const nights = searchQuery?.nights || 7;
    const guests = (searchQuery?.adults || 2) + (searchQuery?.children || 0);

    // Calculate Check-out
    let checkOut = '';
    if (searchQuery?.checkIn) {
        const d = new Date(searchQuery.checkIn);
        d.setDate(d.getDate() + nights);
        checkOut = d.toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US', { day: 'numeric', month: 'short' });
    }

    const dateRange = checkIn ? `${checkIn} - ${checkOut}` : '';

    return (
        <div className="fixed bottom-0 left-0 w-full bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 shadow-[0_-5px_20px_rgba(0,0,0,0.15)] z-[999] transition-transform duration-300 transform translate-y-0">
            <div className="container mx-auto max-w-7xl px-4 py-3 md:py-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-6">

                    {/* Left Side: Hotel Name & Details (Stacked on Mobile) */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 md:mb-0">
                            <h3 className="font-bold text-gray-900 dark:text-white text-base md:text-xl truncate leading-tight">
                                {hotel.name}
                            </h3>
                            {/* Stars (Hidden on very small screens to save space) */}
                            <div className="hidden sm:flex text-yellow-400">
                                {[...Array(Math.max(0, Math.min(5, Math.round(hotel.rating || 0))))].map((_, i) => (
                                    <span key={i} className="text-sm">★</span>
                                ))}
                            </div>
                        </div>
                        {/* Selected Date Info */}
                        {dateRange && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                                <span className="font-medium text-gray-700 dark:text-gray-300">{dateRange}</span>
                                <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                <span>{nights} {language === 'sv' ? 'Nätter' : 'Nights'}</span>
                                <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                <span>{guests} {language === 'sv' ? 'Gäster' : 'Guests'}</span>
                            </div>
                        )}
                    </div>

                    {/* Right Side: Price & Action */}
                    <div className="flex items-center justify-between md:justify-end gap-4 w-full md:w-auto mt-1 md:mt-0 pt-2 md:pt-0 border-t border-gray-100 md:border-t-0 dark:border-gray-800">
                        <div className="text-left md:text-right">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                                {language === 'sv' ? 'TOTALT PRIS' : 'TOTAL PRICE'}
                            </p>
                            <div className="flex items-baseline gap-1.5">
                                <span className="text-2xl md:text-3xl font-black text-blue-950 dark:text-white leading-none">
                                    {Math.round(lowestPrice).toLocaleString('sv-SE')}
                                </span>
                                <span className="text-sm font-bold text-gray-500">{currency}</span>
                            </div>
                        </div>
                        <button
                            onClick={onBook}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 md:px-8 rounded-xl shadow-lg shadow-blue-600/20 transition-all hover:scale-105 active:scale-95 text-sm md:text-base whitespace-nowrap min-w-[140px]"
                        >
                            {language === 'sv' ? 'Välj Rum' : 'Select Room'}
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export const HotelDetail: React.FC<HotelDetailProps> = ({ logApiCall, embeddedHotel, embeddedSearchQuery, onClose }) => {
    // Force cache bust - v3-18092-fix
    useEffect(() => {
        console.log('HotelDetail mounted - v3.1');
    }, []);

    const _FlightDetailsCompact_UNUSED: React.FC<{
        offer: Offer;
        onChangeFlight?: () => void;
        lazyFlightData?: { outbound?: FlightSegment; return?: FlightSegment; loading: boolean };
        realOutbound?: RealFlightOption;
        realInbound?: RealFlightOption;
        onShowAlternativeFlights?: () => void;
    }> = ({ offer, onChangeFlight, lazyFlightData, realOutbound, realInbound, onShowAlternativeFlights }) => {
        // Use offer's flight data if available, otherwise use lazy loaded data OR OVERRIDE with real flights
        let outboundFlight = offer.outboundFlight || lazyFlightData?.outbound;
        let returnFlight = offer.returnFlight || lazyFlightData?.return;

        if (realOutbound) {
            outboundFlight = {
                departureTime: realOutbound.departureTime,
                arrivalTime: realOutbound.arrivalTime,
                departureAirport: realOutbound.departureAirport,
                arrivalAirport: realOutbound.arrivalAirport,
                airline: realOutbound.airline,
                flightDate: realOutbound.departureDate,
                logoUrl: realOutbound.logoUrl,
                duration: realOutbound.duration,
                flightNumber: realOutbound.flightNumber,
                stops: realOutbound.stops || 0
            };
        }
        if (realInbound) {
            returnFlight = {
                departureTime: realInbound.departureTime,
                arrivalTime: realInbound.arrivalTime,
                departureAirport: realInbound.departureAirport,
                arrivalAirport: realInbound.arrivalAirport,
                airline: realInbound.airline,
                flightDate: realInbound.departureDate,
                logoUrl: realInbound.logoUrl,
                duration: realInbound.duration,
                flightNumber: realInbound.flightNumber,
                stops: realInbound.stops || 0
            };
        }
        const isLoadingFlight = lazyFlightData?.loading;

        // Show loading state
        if (isLoadingFlight) {
            return (
                <div className="bg-gradient-to-r from-sky-50 to-blue-50 dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl p-4 my-3 border border-sky-100 dark:border-gray-600">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse">
                            <span className="text-xl">✈️</span>
                        </div>
                        <div className="flex-1">
                            <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse w-32 mb-2"></div>
                            <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded animate-pulse w-48"></div>
                        </div>
                    </div>
                </div>
            );
        }

        if (!outboundFlight) {
            return (
                <div className="bg-gradient-to-r from-sky-50 to-blue-50 dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl p-4 my-3 border border-sky-100 dark:border-gray-600">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-xl">✈️</span>
                        </div>
                        <div className="flex-1">
                            <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                                {language === 'sv' ? 'Komplett Paket' : 'Complete Package'}
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {language === 'sv'
                                    ? 'Inkluderar: Flyg, Hotell, Transfer & Skatter'
                                    : 'Includes: Flight, Hotel, Transfer & Taxes'}
                            </p>
                        </div>
                        <div className="text-right hidden sm:block">
                            <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-full">
                                {language === 'sv' ? 'Paketresa' : 'Package Deal'}
                            </span>
                        </div>
                    </div>
                </div>
            );
        }


        // AI Flight Insight Card
        const FlightInsightCard: React.FC<{
            outbound?: FlightSegment;
            returnFlight?: FlightSegment;
            language: 'en' | 'sv';
        }> = ({ outbound, returnFlight, language }) => {
            const [insight, setInsight] = useState<FlightInsight | null>(null);
            const [loading, setLoading] = useState(false);

            useEffect(() => {
                if (!outbound) return;

                const fetchInsight = async () => {
                    setLoading(true);
                    const result = await getFlightInsight(outbound, returnFlight, language);
                    setInsight(result);
                    setLoading(false);
                };

                fetchInsight();
            }, [outbound?.airline, language]);



            if (loading) {
                return (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-xs text-gray-500">
                                {language === 'sv' ? 'AI analyserar flyget...' : 'AI analyzing flight...'}
                            </span>
                        </div>
                    </div>
                );
            }

            if (!insight) return null;

            const ratingColors = {
                excellent: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                good: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                fair: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
            };

            const ratingIcons = {
                excellent: '🌟',
                good: '✨',
                fair: '💡'
            };

            return (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex items-start gap-3">
                        {/* Professional AI Assistant Icon */}
                        <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg flex items-center justify-center shadow-sm">
                            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                            </svg>
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${ratingColors[insight.rating]}`}>
                                    {ratingIcons[insight.rating]} {insight.rating}
                                </span>
                                <span className="text-sm font-bold text-gray-800 dark:text-gray-200">
                                    {insight.headline}
                                </span>
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                {insight.details}
                            </p>
                            <div className="flex flex-wrap gap-1">
                                {(insight.tags || []).map((tag, i) => (
                                    <span key={i} className="text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            );
        };

        const FlightRow = ({ label, flight, icon, onClick, onShowAlternativeFlights }: { label: string; flight: FlightSegment; icon: string; onClick?: () => void; onShowAlternativeFlights?: () => void }) => (

            <div
                className={`bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-100 dark:border-gray-700 ${onClick ? 'cursor-pointer hover:border-blue-300 hover:shadow-md transition-all' : ''}`}
                onClick={onClick}
            >
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                        {icon} {label}
                        {flight.flightDate && (
                            <span className="ml-2 text-blue-600 dark:text-blue-400 font-normal normal-case">
                                {flight.flightDate}
                            </span>
                        )}
                    </span>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-400">
                            {flight.stops === 0 ? 'Direct' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}
                        </span>
                        {onClick && (
                            <span className="text-[10px] text-blue-500 font-medium">
                                {language === 'sv' ? 'Klicka för detaljer' : 'Click for details'}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    {/* Airline Logo */}
                    <div className="flex flex-col items-center">
                        <img
                            src={flight.logoUrl || 'https://cdn-icons-png.flaticon.com/512/723/723955.png'}
                            className="w-8 h-8 object-contain rounded"
                            alt={flight.airline}
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = 'https://cdn-icons-png.flaticon.com/512/723/723955.png';
                            }}
                        />
                        <span className="text-[9px] text-gray-400 mt-1">{flight.flightNumber}</span>
                    </div>

                    {/* Flight Times */}
                    <div className="flex-1 flex items-center gap-2">
                        {/* Departure */}
                        <div className="text-center">
                            <div className="text-lg font-bold text-gray-900 dark:text-white">
                                {formatFlightDateTime(flight.departureTime, language === 'sv' ? 'sv-SE' : 'en-US').time}
                            </div>
                            <div className="text-[10px] text-gray-500">{flight.departureAirport}</div>
                            {formatFlightDateTime(flight.departureTime, language === 'sv' ? 'sv-SE' : 'en-US').date && (
                                <div className="text-[9px] text-blue-600 font-medium">
                                    {formatFlightDateTime(flight.departureTime, language === 'sv' ? 'sv-SE' : 'en-US').date}
                                </div>
                            )}
                        </div>

                        {/* Flight Path Indicator */}
                        <div className="flex-1 flex items-center px-2">
                            <div className="flex-1 h-[1px] bg-gray-300 dark:bg-gray-600"></div>
                            <div className="px-2 text-[10px] text-gray-400">{formatDuration(flight.duration)}</div>
                            <div className="flex-1 h-[1px] bg-gray-300 dark:bg-gray-600"></div>
                            <svg className="w-4 h-4 text-gray-400 -ml-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                            </svg>
                        </div>

                        {/* Arrival */}
                        <div className="text-center">
                            <div className="text-lg font-bold text-gray-900 dark:text-white">
                                {formatFlightDateTime(flight.arrivalTime, language === 'sv' ? 'sv-SE' : 'en-US').time}
                            </div>
                            <div className="text-[10px] text-gray-500">{flight.arrivalAirport}</div>
                            {formatFlightDateTime(flight.arrivalTime, language === 'sv' ? 'sv-SE' : 'en-US').date && (
                                <div className="text-[9px] text-blue-600 font-medium">
                                    {formatFlightDateTime(flight.arrivalTime, language === 'sv' ? 'sv-SE' : 'en-US').date}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Airline Name & Baggage */}
                    <div className="hidden sm:block text-right">
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{getAirlineName(flight.airline)}</div>
                        <div className="text-[10px] text-cyan-600 dark:text-cyan-400 flex items-center justify-end gap-1 mt-1 font-medium">
                            <span role="img" aria-label="baggage">🧳</span> {flight.baggage || '20 kg'}
                        </div>
                    </div>
                </div>

                {/* Change Flight Button */}
                {onShowAlternativeFlights && (
                    <div className="mt-2 text-right">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onShowAlternativeFlights();
                            }}
                            className="text-xs font-bold text-blue-600 hover:text-blue-800 underline transition-colors"
                        >
                            {language === 'sv' ? 'Ändra flyg' : 'Change flight'}
                        </button>
                    </div>
                )}
            </div>
        );

        // Handler to open flight details modal
        const handleShowFlightDetails = () => {
            setSelectedFlightForModal({ outbound: outboundFlight, return: returnFlight });
            setShowFlightDetailsModal(true);
        };

        return (
            <div className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl p-4 my-3 border border-sky-100 dark:border-gray-600">
                <h4 className="text-sm font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2 mb-3">
                    ✈️ {language === 'sv' ? 'Flygdetaljer' : 'Flight Details'}
                </h4>
                <div className="space-y-3">
                    <FlightRow
                        label={language === 'sv' ? 'Utresa' : 'Outbound'}
                        flight={outboundFlight!}
                        icon="🛫"
                        onClick={handleShowFlightDetails}
                        onShowAlternativeFlights={onShowAlternativeFlights}
                    />
                    {returnFlight && (
                        <FlightRow
                            label={language === 'sv' ? 'Hemresa' : 'Return'}
                            flight={returnFlight}
                            icon="🛬"
                            onClick={handleShowFlightDetails}
                            onShowAlternativeFlights={onShowAlternativeFlights}
                        />
                    )}
                </div>

                {/* AI Flight Insight */}
                <FlightInsightCard outbound={outboundFlight} returnFlight={returnFlight} language={language} />

            </div>

        );
    };


    const { id, destination, slug } = useParams<{ id?: string; destination?: string; slug?: string }>();
    const location = useLocation();
    const { state, search } = location;
    const navigate = useNavigate();
    const { t, currency } = useLocalization();
    const { language } = useContext(LocalizationContext);

    const initialHotel = embeddedHotel || (state?.hotel as Hotel);
    let initialSearchQuery = embeddedSearchQuery || (state?.searchQuery as SearchQuery);

    if (!initialSearchQuery && search) {
        const queryParams = new URLSearchParams(search);
        const qHotelId = queryParams.get('hotelId');
        const qCheckIn = queryParams.get('checkIn');
        const qNights = queryParams.get('nights');
        const qAdults = queryParams.get('adults');
        const qChildrenAgesStr = queryParams.get('childAges');
        const qDepartureId = queryParams.get('departure');
        const qDepartureName = queryParams.get('departureName');
        const qDestinationId = queryParams.get('arrivalId');
        const qDestinationName = queryParams.get('arrivalName');
        const qNat = queryParams.get('nat') || 'SE';
        const qCurr = queryParams.get('curr') || 'SEK';

        if (qCheckIn && qNights && qAdults) {
            let childAges: number[] = [];
            if (qChildrenAgesStr && qChildrenAgesStr !== '{{childAges}}') {
                childAges = qChildrenAgesStr.split(',').map(a => parseInt(a, 10)).filter(n => !isNaN(n));
            }
            
            let numAdults = parseInt(qAdults, 10);
            if (isNaN(numAdults)) numAdults = 2; // Default if template was left as {{adults}}

            initialSearchQuery = {
                type: qDepartureId ? SearchType.PackageTours : SearchType.OnlyHotel,
                departureId: qDepartureId || '',
                departureName: qDepartureName || '',
                destinationId: qDestinationId || '',
                destinationName: qDestinationName || '',
                hotelId: qHotelId || id || '',
                checkIn: qCheckIn,
                nights: parseInt(qNights, 10) || 7,
                rooms: 1,
                nationality: qNat,
                currency: qCurr,
                adults: numAdults,
                children: childAges.length,
                childrenAges: childAges
            };
            console.log('[HotelDetail] 🔍 Created searchQuery from URL params:', initialSearchQuery);
        }
    }

    const stateSelectedOutbound = state?.selectedOutboundFlight as RealFlightOption | null;
    const stateSelectedInbound = state?.selectedReturnFlight as RealFlightOption | null;

    const [searchQuery, setSearchQuery] = useState<SearchQuery | undefined>(initialSearchQuery);
    const [dateChangeCounter, setDateChangeCounter] = useState(0);
    const [isDateUpdating, setIsDateUpdating] = useState(false);
    const [hotel, setHotel] = useState<Hotel | HolidayPackage>(initialHotel);
    const [details, setDetails] = useState<ProductInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeImage, setActiveImage] = useState(initialHotel?.imageUrl || '');

    const [bookingData, setBookingData] = useState<BookingData | null>(null);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [showStickyBar, setShowStickyBar] = useState(false);
    const [instagramUrl, setInstagramUrl] = useState<string | null>(null);

    // Parsed Content State
    const [categorizedContent, setCategorizedContent] = useState<StructuredHotelContent | null>(null);

    // Room Expansion State
    const [expandedRoomId, setExpandedRoomId] = useState<string | null>(null);
    const [roomDetails, setRoomDetails] = useState<Record<string, RoomScrapeData | null>>({});
    const [loadingRoomDetails, setLoadingRoomDetails] = useState(false);
    const [allScrapedRooms, setAllScrapedRooms] = useState<RoomScrapeData[]>([]);

    // Flight Details State - from getOfferDetails API
    const [offerFlightDetails, setOfferFlightDetails] = useState<Record<string, { outbound?: FlightSegment; return?: FlightSegment; outboundBrandedFares?: any[]; returnBrandedFares?: any[]; loading: boolean }>>({});

    // Branded Fare Selection State
    const [selectedOutboundBrandedFare, setSelectedOutboundBrandedFare] = useState<BrandedFare | null>(null);
    const [selectedReturnBrandedFare, setSelectedReturnBrandedFare] = useState<BrandedFare | null>(null);

    // AbortController for all getOfferDetails calls — aborted on booking navigation
    const offerDetailsAbortRef = useRef<AbortController | null>(null);

    // Alternative Flights Modal State
    const [showFlightModal, setShowFlightModal] = useState(false);
    const [selectedOfferForFlight, setSelectedOfferForFlight] = useState<Offer | null>(null);
    const [alternativeOffers, setAlternativeOffers] = useState<Offer[]>([]);

    // FlightOptionsModal State (Diğer Uçuşlar Feature)
    const [showFlightOptionsModal, setShowFlightOptionsModal] = useState(false);
    const [flightOptionsOfferId, setFlightOptionsOfferId] = useState<string>('');

    // Flight Details Modal State (for clicking on flight info in rooms)
    const [showFlightDetailsModal, setShowFlightDetailsModal] = useState(false);
    const [selectedFlightForModal, setSelectedFlightForModal] = useState<{ outbound?: FlightSegment; return?: FlightSegment } | null>(null);

    // Google Hotel Details State (from SERP API)
    const [googleDetails, setGoogleDetails] = useState<GoogleHotelDetails | null>(null);
    const [googleDetailsLoading, setGoogleDetailsLoading] = useState(false);

    // Real SanFlight Flights State
    const [realFlights, setRealFlights] = useState<{ outbound: RealFlightOption[]; inbound: RealFlightOption[] }>({ outbound: [], inbound: [] });
    const [realFlightsLoading, setRealFlightsLoading] = useState(false);
    const [selectedOutboundFlight, setSelectedOutboundFlight] = useState<RealFlightOption | null>(null);
    const [selectedInboundFlight, setSelectedInboundFlight] = useState<RealFlightOption | null>(null);

    // Scraping State
    const [isScrapingRooms, setIsScrapingRooms] = useState(false);

    // Cached Room Data from HotelCache (admin-curated rooms, images, descriptions)
    const [cachedRoomData, setCachedRoomData] = useState<{
        hasCache: boolean;
        rooms: Array<{
            roomTypeName: string;
            description: string;
            highlights: string[];
            amenities: string[];
            images: string[];
            maxAdults?: number;
            maxChildren?: number;
        }>;
        boardTypes: Array<{ boardTypeName: string; description: string }>;
    }>({ hasCache: false, rooms: [], boardTypes: [] });

    // Determine if this is a package tour (with flights) or hotel-only search
    const isPackageTour = searchQuery?.type === SearchType.PackageTours;

    // Cache Data Integration - Prioritize cached data for performance
    const [dataSource, setDataSource] = useState<'cache' | 'live' | 'loading'>('loading');
    const { cachedData, cacheMetadata, isCached, isLoading: cacheLoading } = useCachedHotelData(
        hotel?.productId || hotel?.id || id
    );

    // Sync embeddedHotel prop with state
    useEffect(() => {
        if (embeddedHotel) {
            setHotel(embeddedHotel);
        }
    }, [embeddedHotel]);

    // Set selected flights from state (when returning from FlightSelection)
    useEffect(() => {
        if (stateSelectedOutbound) {
            setSelectedOutboundFlight(stateSelectedOutbound);
        }
        if (stateSelectedInbound) {
            setSelectedInboundFlight(stateSelectedInbound);
        }
    }, [stateSelectedOutbound, stateSelectedInbound]);

    // Sync URL offerId flights to selected flights state
    useEffect(() => {
        if (!searchQuery) return;
        const queryParams = new URLSearchParams(location.search);
        const urlOfferId = queryParams.get('offerId') || queryParams.get('outboundId');
        if (!urlOfferId) return;

        const flightData = offerFlightDetails[urlOfferId];
        if (flightData && !flightData.loading) {
            if (flightData.outbound && !selectedOutboundFlight) {
                const outSeg = flightData.outbound;
                setSelectedOutboundFlight({
                    id: `${urlOfferId}-outbound`,
                    offerId: urlOfferId,
                    direction: 'outbound',
                    airline: typeof outSeg.airline === 'object' ? (outSeg.airline?.name || 'Charter') : (outSeg.airline || 'Charter'),
                    airlineCode: outSeg.airlineCode || '',
                    flightNumber: outSeg.flightNumber || '',
                    departureAirport: outSeg.departureAirport || '',
                    departureAirportCode: outSeg.departureAirportCode || '',
                    departureTime: outSeg.departureTime || '',
                    departureDate: outSeg.flightDate || '',
                    arrivalAirport: outSeg.arrivalAirport || '',
                    arrivalAirportCode: outSeg.arrivalAirportCode || '',
                    arrivalTime: outSeg.arrivalTime || '',
                    duration: outSeg.duration || '',
                    stops: outSeg.stops || 0,
                    priceDifference: 0,
                    logoUrl: outSeg.logoUrl || ''
                });
            }
            if (flightData.return && !selectedInboundFlight) {
                const retSeg = flightData.return;
                setSelectedInboundFlight({
                    id: `${urlOfferId}-inbound`,
                    offerId: urlOfferId,
                    direction: 'return',
                    airline: typeof retSeg.airline === 'object' ? (retSeg.airline?.name || 'Charter') : (retSeg.airline || 'Charter'),
                    airlineCode: retSeg.airlineCode || '',
                    flightNumber: retSeg.flightNumber || '',
                    departureAirport: retSeg.departureAirport || '',
                    departureAirportCode: retSeg.departureAirportCode || '',
                    departureTime: retSeg.departureTime || '',
                    departureDate: retSeg.flightDate || '',
                    arrivalAirport: retSeg.arrivalAirport || '',
                    arrivalAirportCode: retSeg.arrivalAirportCode || '',
                    arrivalTime: retSeg.arrivalTime || '',
                    duration: retSeg.duration || '',
                    stops: retSeg.stops || 0,
                    priceDifference: 0,
                    logoUrl: retSeg.logoUrl || ''
                });
            }
        }
    }, [offerFlightDetails, selectedOutboundFlight, selectedInboundFlight, searchQuery, location.search]);


    const galleryImages = useMemo(() => {
        // === ADMIN IMAGE PRIORITY ===
        // If admin has uploaded images via admin panel (localImages), use them FIRST
        const localImages: string[] = ((details as any)?.localImages || []).map((img: any) =>
            typeof img === 'string' ? img : (img.url || img)
        ).filter(Boolean);
        const localThumbnail = (details as any)?.localThumbnail;

        // Collect Tourvisio API images
        const tourvisioImages = details?.images && details.images.length > 0
            ? details.images
            : (hotel?.images && hotel.images.length > 0
                ? hotel.images.map((img: any) => typeof img === 'string' ? img : img.url)
                : (hotel?.imageUrl ? [hotel.imageUrl] : []));

        // Google Hotel images from SERP API - POLICY:
        // Only use Google images if:
        // 1. Admin has approved them (hotel.approvedGoogleImages = true), OR
        // 2. No other images available (AI Planner fallback case)
        const hasLocalOrTourvisio = localImages.length > 0 || tourvisioImages.length > 0;
        const shouldUseGoogleImages = (hotel as any)?.approvedGoogleImages === true || !hasLocalOrTourvisio;
        const googleImages = shouldUseGoogleImages
            ? (googleDetails?.images?.map(img => img.original).filter(Boolean) || [])
            : [];

        // Combine: LOCAL FIRST (admin picks), then tourvisio, then Google (avoid duplicates)
        const allImages: string[] = [];

        // 1. Admin-selected local thumbnail as first image (hero)
        if (localThumbnail && !allImages.includes(localThumbnail)) {
            allImages.push(localThumbnail);
        }

        // 2. Admin-uploaded local images
        localImages.forEach(img => {
            if (img && !allImages.includes(img)) allImages.push(img);
        });

        // 3. Tourvisio API images (only if no admin images take priority)
        tourvisioImages.forEach(img => {
            if (img && !allImages.includes(img)) allImages.push(img);
        });

        // 4. Google images last
        googleImages.forEach(img => {
            if (img && !allImages.includes(img)) allImages.push(img);
        });

        // Process all images: fix relative paths and strip pre-existing size params
        // so that getHeroImageUrl() can apply correct 1920x1080 sizing when used as hero
        return allImages.map(img => {
            const fixed = getImageUrl(img) || img;
            // Strip any existing width/height/mode params — hero/gallery will re-apply as needed
            return stripImageParams(fixed) || fixed;
        }).filter(Boolean) as string[];
    }, [details, hotel, googleDetails]);

    useEffect(() => {
        // Safe check for hotel existence
        if (!hotel) {
            // Try slug-based resolution first (new URL format: /hotel/:destination/:slug)
            if (slug) {
                (async () => {
                    const resolvedHotel = await getHotelBySlug(slug);
                    if (resolvedHotel) {
                        setHotel(resolvedHotel);
                        if (resolvedHotel.imageUrl) setActiveImage(resolvedHotel.imageUrl);
                        return;
                    }
                    // Fallback: try the slug as an ID
                    const fallback = await getHotelFromCMS(slug);
                    if (fallback) {
                        setHotel(fallback);
                        if (fallback.imageUrl) setActiveImage(fallback.imageUrl);
                        return;
                    }
                    
                    // Deep-link Fallback
                    const urlParams = new URLSearchParams(window.location.search);
                    const qHotelId = urlParams.get('hotelId');
                    if (qHotelId) {
                        console.log('[HotelDetail] ⚠️ Hotel not in CMS, but found hotelId in URL, creating stub...');
                        setHotel({
                            id: qHotelId,
                            productId: qHotelId,
                            provider: 1,
                            name: slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
                            location: { city: destination || '', country: '' },
                            rating: 0,
                            pricePerNight: 0,
                            currency: 'EUR',
                            imageUrl: '',
                            board: '',
                            roomType: '',
                            themes: [],
                            facilities: [],
                            offers: []
                        } as any);
                        return;
                    }
                    
                    navigate('/');
                })();
                return;
            }
            if (id) {
                (async () => {
                    const salvagedHotel = await getHotelFromCMS(id);
                    if (salvagedHotel) {
                        setHotel(salvagedHotel);
                        if (salvagedHotel.imageUrl) setActiveImage(salvagedHotel.imageUrl);
                        return;
                    }
                    
                    // Deep-link Fallback
                    const urlParams = new URLSearchParams(window.location.search);
                    const qHotelId = urlParams.get('hotelId') || id;
                    if (qHotelId) {
                        console.log('[HotelDetail] ⚠️ Hotel not in CMS, but found hotelId, creating stub...');
                        setHotel({
                            id: qHotelId,
                            productId: qHotelId,
                            provider: 1,
                            name: 'Hotel',
                            location: { city: destination || '', country: '' },
                            rating: 0,
                            pricePerNight: 0,
                            currency: 'EUR',
                            imageUrl: '',
                            board: '',
                            roomType: '',
                            themes: [],
                            facilities: [],
                            offers: []
                        } as any);
                        return;
                    }
                    
                    navigate('/');
                })();
                return;
            }
            // If still no hotel, go back
            navigate('/');
            return;
        }

        const fetchDetails = async () => {
            // DISABLE CACHE FOR FRONTEND - Per user request (Cache only for Admin/API)
            const useCache = false;

            // Check if we have cached data first
            if (useCache && isCached && cachedData && !cacheLoading) {
                console.log('[HotelDetail] ✅ Using CACHED data for:', cachedData.name);
                setDataSource('cache');

                // Apply cached data - but preserve existing images if cache has none
                let cachedImages = cachedData.images || [];

                // If cached images are empty, try to get from thumbnailUrl or existing hotel images
                if (cachedImages.length === 0) {
                    console.log('[HotelDetail] ⚠️ Cache has no images, using fallbacks...');

                    // Try cachedData.thumbnailUrl
                    if (cachedData.thumbnailUrl) {
                        cachedImages = [cachedData.thumbnailUrl];
                        console.log('[HotelDetail] Using cached thumbnailUrl');
                    }
                    // Try initial hotel images
                    else if (hotel?.imageUrl) {
                        cachedImages = [hotel.imageUrl];
                        console.log('[HotelDetail] Using hotel.imageUrl');
                    }
                    else if (hotel?.thumbnailUrl) {
                        cachedImages = [hotel.thumbnailUrl];
                        console.log('[HotelDetail] Using hotel.thumbnailUrl');
                    }

                    // If still no images, DON'T use cache - fall through to live API
                    else {
                        console.log('[HotelDetail] No images in cache or hotel, falling back to live API...');
                        // Continue to live API fetch below (don't return early)
                    }
                }

                // Only use cache if we have images
                if (cachedImages.length > 0) {
                    setDetails({
                        description: cachedData.description || '',
                        images: cachedImages,
                        facilities: cachedData.facilities || []
                    });
                    setActiveImage(cachedImages[0]);

                    // Update hotel with cached offers if available
                    if (cachedData.offers && cachedData.offers.length > 0) {
                        setHotel(prev => ({
                            ...prev,
                            offers: cachedData.offers.map(o => ({
                                id: o.offerId,
                                roomType: o.roomType,
                                board: o.board,
                                price: o.price,
                                currency: o.currency,
                                isRefundable: o.isRefundable,
                                cancellationPolicy: o.cancellationPolicy
                            })) as any,
                            pricePerNight: cachedData.lowestPrice,
                            currency: cachedData.lowestCurrency
                        }));
                    }

                    setIsLoading(false);

                    // Still fetch supplementary data (booking, instagram) in background
                    fetchBookingData(hotel.name, hotel.location?.city || '', logApiCall).then(setBookingData);
                    // Use DB instagramUrl first, only fallback to apify lookup if empty
                    if (hotel.instagramUrl && !hotel.instagramUrl.includes('/explore/tags/')) {
                        setInstagramUrl(hotel.instagramUrl);
                    } else {
                        findInstagramHandle(hotel.name, hotel.location?.city || '').then(setInstagramUrl);
                    }

                    return; // Only return if we have images
                }
            }

            // No cached data - fall back to live API
            if (hotel.productId) {
                console.log('[HotelDetail] 📡 Fetching LIVE data for:', hotel.name);
                setDataSource('live');

                const provider = hotel.provider !== undefined ? hotel.provider : 1;
                const info = await getProductInfo(2, hotel.productId, provider, 'en-US', logApiCall);
                const savedHotel = await getHotelFromCMS(hotel.id);
                let finalImages = info?.images || [];

                if (savedHotel && savedHotel.isEdited && savedHotel.images && savedHotel.images.length > 0) {
                    finalImages = savedHotel.images;
                }

                // Preserve admin-selected localImages and localThumbnail from getProductInfo merge
                const detailsPayload: any = info ? {
                    ...info,
                    description: info.description,
                    images: finalImages,
                    coordinates: info.coordinates || (hotel.location?.latitude && hotel.location?.longitude ? {
                        latitude: hotel.location.latitude,
                        longitude: hotel.location.longitude
                    } : undefined)
                } : {
                    description: '',
                    images: finalImages,
                    facilities: [],
                    coordinates: hotel.location?.latitude && hotel.location?.longitude ? {
                        latitude: hotel.location.latitude,
                        longitude: hotel.location.longitude
                    } : undefined
                };

                // Carry forward localImages/localThumbnail from API merge
                if (info?.localImages) detailsPayload.localImages = info.localImages;
                if (info?.localThumbnail) detailsPayload.localThumbnail = info.localThumbnail;

                setDetails(detailsPayload);

                // Admin-selected image takes priority for hero
                if (info?.localThumbnail) {
                    setActiveImage(info.localThumbnail);
                } else if (info?.localImages?.length > 0) {
                    const firstLocal = typeof info.localImages[0] === 'string' ? info.localImages[0] : info.localImages[0]?.url;
                    if (firstLocal) setActiveImage(firstLocal);
                } else if (finalImages.length > 0) {
                    setActiveImage(finalImages[0]);
                }

                // Auto-save to CMS for Admin Panel access
                if (info && hotel.id) {
                    saveHotelToCMS({ // async, fire-and-forget
                        ...hotel,
                        description: info.description || hotel.description,
                        images: finalImages,
                        facilities: (info.facilities && info.facilities.length > 0) ? info.facilities : (hotel.facilities || []),
                        isEdited: false // Keep as false to allow future API updates unless manual edit occurs
                    });
                }

                const bData = await fetchBookingData(hotel.name, hotel.location?.city || '', logApiCall);
                setBookingData(bData);

                // Fetch Instagram — prioritize DB field
                if (hotel.instagramUrl && !hotel.instagramUrl.includes('/explore/tags/')) {
                    setInstagramUrl(hotel.instagramUrl);
                } else {
                    const ig = await findInstagramHandle(hotel.name, hotel.location?.city || '');
                    setInstagramUrl(ig);
                }

                // Fetch Real Room Photos via Gemini Hotel Scraper
                setIsScrapingRooms(true);
                fetch(`/api/tourvisio/room-details/batch`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        hotelName: hotel.name,
                        city: hotel.location?.city || '',
                        roomTypes: hotel.offers?.slice(0, 5).map(o => o.roomType) || [],
                        language: 'en'
                    })
                })
                    .then(res => res.json())
                    .then(data => {
                        if (data.success && data.rooms) {
                            // Convert to RoomScrapeData format
                            const rooms = data.rooms.map((r: any) => ({
                                name: r.name,
                                description: r.description || '',
                                photos: r.photos || [],
                                bedType: r.bedType || '',
                                size: r.size || '',
                                source: r.source
                            }));
                            setAllScrapedRooms(rooms);
                        }
                        setIsScrapingRooms(false);
                    })
                    .catch(err => {
                        console.error("Room fetch error:", err);
                        setIsScrapingRooms(false);
                    });

                // Fetch Google Hotel Details from SERP API
                setGoogleDetailsLoading(true);
                getGoogleHotelDetails(hotel.name, hotel.location?.city)
                    .then(gDetails => {
                        if (gDetails) {
                            console.log('✅ Google Hotel Details loaded:', gDetails.name, 'Images:', gDetails.images.length, 'Rooms:', gDetails.rooms?.length || 0);
                            setGoogleDetails(gDetails);
                        }
                        setGoogleDetailsLoading(false);
                    })
                    .catch(err => {
                        console.error("Google Hotel Details error:", err);
                        setGoogleDetailsLoading(false);
                    });

                // Fetch Real Flights from SanFlight (ProductType 3) — ONLY for Package Tours
                if (isPackageTour && searchQuery && hotel.offers && hotel.offers.length > 0) {
                    const firstOffer = hotel.offers[0];
                    const outbound = firstOffer.outboundFlight;
                    const returnFlight = firstOffer.returnFlight;

                    // Get departure/arrival airports from transportations or use defaults
                    const depAirport = outbound?.departureAirport || 'ARN';
                    const arrAirport = outbound?.arrivalAirport || 'BJV';

                    // Calculate dates from searchQuery
                    const checkIn = searchQuery.checkIn;
                    const nights = searchQuery.nights || 7;
                    const checkInDate = new Date(checkIn);
                    const checkOutDate = new Date(checkInDate);
                    checkOutDate.setDate(checkOutDate.getDate() + nights);
                    const checkOut = checkOutDate.toISOString().split('T')[0];

                    console.log('✈️ Fetching real SanFlight data:', depAirport, '→', arrAirport, checkIn, 'to', checkOut);

                    setRealFlightsLoading(true);
                    searchRealFlights(
                        depAirport,
                        arrAirport,
                        checkIn,
                        checkOut,
                        searchQuery.adults || 2,
                        searchQuery.children || 0,
                        logApiCall
                    )
                        .then(flights => {
                            console.log('✅ Real flights loaded:', flights.outbound.length, 'outbound,', flights.inbound.length, 'inbound');
                            setRealFlights(flights);

                            // Auto-select first flights if available
                            if (flights.outbound.length > 0) {
                                setSelectedOutboundFlight(flights.outbound[0]);
                            }
                            if (flights.inbound.length > 0) {
                                setSelectedInboundFlight(flights.inbound[0]);
                            }
                            setRealFlightsLoading(false);
                        })
                        .catch(err => {
                            console.error("Real flights fetch error:", err);
                            setRealFlightsLoading(false);
                        });
                }
            }
            setIsLoading(false);
        };
        fetchDetails();
    }, [hotel?.id, navigate, logApiCall, language, isCached, cachedData, cacheLoading]);

    // Fetch offers when search query changes - handles BOTH PackageTours AND OnlyHotel
    useEffect(() => {
        if (!hotel) return;
        if (!searchQuery) return;

        // Skip if hotel already has real offers loaded (not just synthetic initial placeholders)
        // BUT allow re-fetch when dateChangeCounter > 0 (user selected alternative date)
        if (dateChangeCounter === 0) {
            const hasRealOffers = hotel.offers && hotel.offers.length > 0 && hotel.offers.some((o: any) => o.id && o.id !== 'initial');
            if (hasRealOffers) {
                console.log('[HotelDetail] ✅ Hotel already has', hotel.offers.length, 'real offers, skipping re-fetch');
                return;
            }
        }

        const fetchOffers = async () => {
            console.log('[HotelDetail] 🔄 Fetching offers for hotel:', hotel.id, 'type:', searchQuery.type);

            if (searchQuery.type === SearchType.PackageTours) {
                if (!searchQuery.departureId) return;

                // searchWithCache kullanarak uçuş bilgileri dahil al
                const result = await searchWithCache({ ...searchQuery, hotelId: hotel.id }, language, logApiCall);

                // Process hotels (now in result.hotels)
                if (result.hotels && result.hotels.length > 0) {
                    const updatedHotel = result.hotels[0] as HolidayPackage;
                    console.log('[HotelDetail] ✅ Got', updatedHotel.offers?.length || 0, 'offers from searchWithCache');
                    setHotel(prev => ({
                        ...prev,
                        offers: updatedHotel.offers,
                        pricePerNight: updatedHotel.pricePerNight,
                        currency: updatedHotel.currency,
                    }));
                } else {
                    console.log('[HotelDetail] ⚠️ searchWithCache returned 0 hotels');
                }

                // Process alternativeFlights (new from backend) — ONLY for Package Tours
                if (isPackageTour && result.alternativeFlights && result.alternativeFlights.length > 0) {
                    console.log('✈️ Alternative flights received:', result.alternativeFlights.length, 'flights');

                    // Separate into outbound and inbound flights
                    const outboundFlights = result.alternativeFlights.filter(f => !f.isReturn);
                    const inboundFlights = result.alternativeFlights.filter(f => f.isReturn);

                    console.log('✈️ Outbound:', outboundFlights.length, 'Inbound:', inboundFlights.length);

                    setRealFlights({
                        outbound: outboundFlights,
                        inbound: inboundFlights
                    });

                    // Auto-select first flights if not already selected
                    if (outboundFlights.length > 0 && !selectedOutboundFlight) {
                        setSelectedOutboundFlight(outboundFlights[0]);
                    }
                    if (inboundFlights.length > 0 && !selectedInboundFlight) {
                        setSelectedInboundFlight(inboundFlights[0]);
                    }
                }
            } else {
                // OnlyHotel or any other type - do a direct priceSearch via searchTourvisio
                console.log('[HotelDetail] 🏨 Hotel-only search, fetching offers via searchTourvisio');
                try {
                    const hotels = await searchTourvisio({ ...searchQuery, hotelId: hotel.id }, language, logApiCall);
                    if (hotels && hotels.length > 0) {
                        const updatedHotel = hotels[0] as HolidayPackage;
                        console.log('[HotelDetail] ✅ Got', updatedHotel.offers?.length || 0, 'offers from searchTourvisio');
                        setHotel(prev => ({
                            ...prev,
                            offers: updatedHotel.offers,
                            pricePerNight: updatedHotel.pricePerNight,
                            currency: updatedHotel.currency,
                        }));
                    } else {
                        console.log('[HotelDetail] ⚠️ searchTourvisio returned 0 hotels');
                    }
                } catch (e) {
                    console.error('[HotelDetail] ❌ searchTourvisio failed:', e);
                }
            }
        };
        fetchOffers().finally(() => setIsDateUpdating(false));
    }, [searchQuery, hotel?.id, language, logApiCall, dateChangeCounter]);

    useEffect(() => {
        const handleScroll = () => setShowStickyBar(window.scrollY > 600);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Effect to parse description whenever it changes
    useEffect(() => {
        if (!hotel) return; // FIX: Prevent access if hotel is undefined

        const rawDesc = (language === 'sv' && hotel.swedishDescription)
            ? hotel.swedishDescription
            : (details?.description || hotel.description);

        // Prioritize Edited Description from CMS if available (async)
        const loadDescription = async () => {
            const savedHotel = await getHotelFromCMS(hotel.id);
            let finalRawDesc = rawDesc;
            if (savedHotel && savedHotel.isEdited && savedHotel.description) {
                finalRawDesc = savedHotel.description;
            }

            let text = "";
            if (typeof finalRawDesc === 'string') {
                text = finalRawDesc;
            } else if (finalRawDesc) {
                text = finalRawDesc[language] || finalRawDesc.en || finalRawDesc.sv || "";
            }

            if (text) {
                // Try to use AI parsing first
                getStructuredHotelDescription(text, language).then(struct => {
                    if (struct) {
                        setCategorizedContent(struct);
                    } else {
                        setCategorizedContent(parseDescriptionLocally(text));
                    }
                });
            }
        };
        loadDescription();
    }, [details, hotel, language]);

    // Handler for fetching alternative flights using Paging Data
    const handleFetchAlternativeFlights = async (searchId: string, offer: Offer) => {
        if (!searchId) return;

        console.log('✈️ Fetching alternative flights for searchId:', searchId);

        // Show loading state/modal immediately
        setSelectedOfferForFlight(offer);
        setAlternativeOffers([]); // Clear previous results
        setShowFlightModal(true);

        try {
            // Using ProductType 3 (Flight Option 2) as per requirements for alternative flights
            // We request page 1, distinct from the initial 'PriceSearch'
            const response = await getPagingData(searchId, 1, 1, logApiCall); // sortType=1 (price sort)

            if (response && response.results && response.results.length > 0) {
                // getPagingData returns SearchResponse with mapped hotel/offer results
                const rawResults = response.results;

                console.log('✅ Received paging results:', rawResults.length);

                // CRITICAL FIX: Collect ALL unique offer IDs from ALL hotel results' offers arrays:
                // When searching for a specific hotel, getPagingData may return the same hotel
                // multiple times with different offers. We need ALL unique offer IDs.
                const uniqueOfferMap = new Map<string, { offerId: string, offer: any, source: any }>();
                for (const item of rawResults) {
                    const hotelOffers = (item as any).offers || [];
                    for (const ho of hotelOffers) {
                        const oid = ho.id || ho.offerId;
                        if (oid && !uniqueOfferMap.has(oid)) {
                            uniqueOfferMap.set(oid, { offerId: oid, offer: ho, source: item });
                        }
                    }
                }

                console.log(`📋 Found ${uniqueOfferMap.size} unique offer IDs across ${rawResults.length} hotel results`);

                const detailedOffers: Offer[] = [];
                const BATCH_SIZE = 5;
                const allUniqueOffers = Array.from(uniqueOfferMap.values());

                for (let i = 0; i < Math.min(allUniqueOffers.length, 20); i += BATCH_SIZE) {
                    const batch = allUniqueOffers.slice(i, i + BATCH_SIZE);
                    const processed = await Promise.all(batch.map(async ({ offerId: oid, offer: ho }) => {
                        try {
                            console.log(`🔍 Fetching details for unique offer ID: ${oid}`);

                            // FETCH DETAILS to get flight information
                            const details = await getOfferDetails(oid, logApiCall, offerDetailsAbortRef.current?.signal);

                            if (details) {
                                return {
                                    id: oid,
                                    name: offer.name,
                                    board: ho.board || offer.board,
                                    roomType: ho.roomType || offer.roomType,
                                    price: details.price?.amount || ho.price || offer.price,
                                    currency: details.price?.currency || ho.currency || offer.currency,
                                    checkIn: offer.checkIn,
                                    night: offer.night,
                                    available: true,
                                    outboundFlight: details.outboundFlight,
                                    returnFlight: details.returnFlight
                                } as Offer;
                            }
                            return null;
                        } catch (e) {
                            console.error("Detail fetch failed for offer " + oid, e);
                            return null;
                        }
                    }));
                    detailedOffers.push(...processed.filter(Boolean) as Offer[]);
                }

                setAlternativeOffers(detailedOffers);
            } else {
                console.log('⚠️ No results found in paging response');
            }

        } catch (error) {
            console.error("Failed to fetch alternative flights:", error);
        }
    };

    const handleBook = (offerToBook: Offer) => {
        // Abort all pending getOfferDetails calls to prevent conflict with beginTransaction
        offerDetailsAbortRef.current?.abort();
        offerDetailsAbortRef.current = null;
        const query = searchQuery || { type: SearchType.PackageTours, departureId: '', destinationId: '', checkIn: offerToBook.checkIn, nights: offerToBook.night, rooms: 1, nationality: 'SE', currency: offerToBook.currency, adults: 2, children: 0, childrenAges: [] };
        
        const finalOffer = { ...offerToBook };
        const isManuallySelected = selectedOfferForFlight && selectedOfferForFlight.id === offerToBook.id;

        if (isManuallySelected && (selectedOutboundFlight || selectedInboundFlight)) {
            // Apply selected flights
            finalOffer.outboundFlight = selectedOutboundFlight ? {
                flightId: selectedOutboundFlight.id,
                flightOfferId: selectedOutboundFlight.offerId,
                flightNumber: selectedOutboundFlight.flightNumber,
                airline: selectedOutboundFlight.airline,
                departureAirport: selectedOutboundFlight.departureAirport,
                arrivalAirport: selectedOutboundFlight.arrivalAirport,
                departureTime: selectedOutboundFlight.departureTime,
                arrivalTime: selectedOutboundFlight.arrivalTime,
                departureDate: selectedOutboundFlight.departureDate,
                brandedFareId: selectedOutboundFlight.brandedFareId,
            } as any : finalOffer.outboundFlight;
            
            finalOffer.returnFlight = selectedInboundFlight ? {
                flightId: selectedInboundFlight.id,
                flightOfferId: selectedInboundFlight.offerId,
                flightNumber: selectedInboundFlight.flightNumber,
                airline: selectedInboundFlight.airline,
                departureAirport: selectedInboundFlight.departureAirport,
                arrivalAirport: selectedInboundFlight.arrivalAirport,
                departureTime: selectedInboundFlight.departureTime,
                arrivalTime: selectedInboundFlight.arrivalTime,
                departureDate: selectedInboundFlight.departureDate,
                brandedFareId: selectedInboundFlight.brandedFareId,
            } as any : finalOffer.returnFlight;

            // Also append the price difference to the total offer price if it's not already added
            const totalDelta = (selectedOutboundFlight?.priceDifference || 0) + (selectedInboundFlight?.priceDifference || 0);
            if (totalDelta !== 0) {
                finalOffer.price += totalDelta;
            }
        } else {
            // Fallback: merge dynamically auto-fetched default flight details if available
            const details = offerFlightDetails[offerToBook.id];
            if (details && !details.loading) {
                if (details.outbound) {
                    finalOffer.outboundFlight = {
                        ...finalOffer.outboundFlight,
                        ...details.outbound
                    };
                }
                if (details.return) {
                    finalOffer.returnFlight = {
                        ...finalOffer.returnFlight,
                        ...details.return
                    };
                }
            }
        }

        navigate('/booking', { state: { hotel, offer: finalOffer, searchQuery: query } });
    };

    const handleOpenLightbox = (index: number) => { setLightboxIndex(index); setIsLightboxOpen(true); };
    const handleCloseLightbox = () => { setIsLightboxOpen(false); };

    const handlePrevImage = () => {
        setLightboxIndex((prev) => (prev > 0 ? prev - 1 : galleryImages.length - 1));
    };

    const handleNextImage = () => {
        setLightboxIndex((prev) => (prev < galleryImages.length - 1 ? prev + 1 : 0));
    };

    // Fuzzy matching for room types
    const findMatchingScrapedRoom = useCallback((offerRoomName: string): RoomScrapeData | null => {
        if (!offerRoomName || !allScrapedRooms || allScrapedRooms.length === 0) return null;

        const normalize = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const target = normalize(offerRoomName);

        // 1. Direct match (normalized)
        let match = allScrapedRooms.find(r => normalize(r.name) === target);
        if (match) return match;

        // 2. Contains match (e.g. "Standard Sea View" matches "Double Room Sea View")
        // Check for key keywords: "Sea View", "Land View", "Suite", "Family"
        const keywords = ["sea view", "land view", "garden view", "suite", "family", "standard", "superior", "deluxe"];
        const targetKeywords = keywords.filter(k => (offerRoomName || '').toLowerCase().includes(k));

        if (targetKeywords.length > 0) {
            // Find scrape result that contains MOST of these keywords
            let bestScore = 0;
            let bestMatch = null;

            allScrapedRooms.forEach(room => {
                const roomNameLower = (room.name || '').toLowerCase();
                let score = 0;
                targetKeywords.forEach(k => {
                    if (roomNameLower.includes(k)) score++;
                });

                if (score > bestScore) {
                    bestScore = score;
                    bestMatch = room;
                }
            });

            // Threshold: needs to match at least some keywords if specific ones were requested
            if (bestScore > 0) return bestMatch;
        }

        // Fallback: If "Standard" is in name, return "Standard Double"
        if ((offerRoomName || '').toLowerCase().includes("standard")) {
            return allScrapedRooms.find(r => (r.name || '').toLowerCase().includes("standard")) || null;
        }

        return null;
    }, [allScrapedRooms]);

    const toggleRoomDetails = (offerId: string) => {
        if (expandedRoomId === offerId) {
            setExpandedRoomId(null);
        } else {
            setExpandedRoomId(offerId);
        }
    };

    // Effect to update room details when expanded ID changes OR when data arrives
    useEffect(() => {
        if (!expandedRoomId) return;

        // Find the offer object
        const offer = roomOptions.find(o => o.id === expandedRoomId);
        if (!offer) return;

        // If we already have specific details, do nothing
        if (roomDetails[expandedRoomId]) return;

        if (allScrapedRooms.length > 0) {
            // Data is ready, try to match
            const match = findMatchingScrapedRoom(offer.roomType);
            setRoomDetails(prev => ({ ...prev, [expandedRoomId]: match }));
        } else if (!isScrapingRooms) {
            // Data fetch finished but returned nothing
            setRoomDetails(prev => ({ ...prev, [expandedRoomId]: null }));
        }
        // If isScrapingRooms is true, we wait. The effect will re-run when allScrapedRooms updates.

    }, [expandedRoomId, allScrapedRooms, isScrapingRooms, findMatchingScrapedRoom]);

    // Effect to fetch flight details from getOfferDetails when expanded
    useEffect(() => {
        if (!expandedRoomId || !hotel?.offers) return;

        const offer = hotel.offers.find(o => o.id === expandedRoomId);
        if (!offer) return;


        // Skip if already has COMPLETE flight info (with actual times) or already loaded/loading
        // priceSearch returns flights with empty departureTime — these are incomplete and need detail fetch
        const hasCompleteFlight = offer.outboundFlight && offer.outboundFlight.departureTime;
        if (hasCompleteFlight || offerFlightDetails[expandedRoomId]) return;

        const fetchFlightDetails = async () => {
            console.log('🚀 Fetching flight details for offer:', expandedRoomId);
            setOfferFlightDetails(prev => ({ ...prev, [expandedRoomId]: { loading: true } }));

            try {
                const details = await getOfferDetails(expandedRoomId, logApiCall, offerDetailsAbortRef.current?.signal);

                if (details) {
                    setOfferFlightDetails(prev => ({
                        ...prev,
                        [expandedRoomId]: {
                            outbound: details.outboundFlight,
                            return: details.returnFlight,
                            outboundBrandedFares: details.outboundBrandedFares,
                            returnBrandedFares: details.returnBrandedFares,
                            loading: false
                        }
                    }));
                } else {
                    setOfferFlightDetails(prev => ({ ...prev, [expandedRoomId]: { loading: false } }));
                }
            } catch (err) {
                console.error('Failed to fetch flight details:', err);
                setOfferFlightDetails(prev => ({ ...prev, [expandedRoomId]: { loading: false } }));
            }
        };

        fetchFlightDetails();
    }, [expandedRoomId, hotel?.offers, offerFlightDetails, logApiCall]);

    // Auto-fetch flight details for ALL offers on page load
    // This ensures every room option shows real flight info
    // instead of the generic "Complete Package" fallback
    useEffect(() => {
        if (!hotel?.offers || hotel.offers.length === 0) return;

        // Find offers that don't have COMPLETE flight data (with actual times) and aren't already loading
        // priceSearch returns flights with airline/airport but empty departureTime — these need detail fetch
        const offersNeedingFlights = hotel.offers.filter(offer =>
            offer.id && !(offer.outboundFlight && offer.outboundFlight.departureTime) && !offerFlightDetails[offer.id]
        );

        if (offersNeedingFlights.length === 0) return;

        // Create a new AbortController for this batch
        const abortController = new AbortController();
        offerDetailsAbortRef.current = abortController;

        const fetchAllFlightDetails = async () => {
            console.log(`✈️ Auto-fetching flight details for ${offersNeedingFlights.length} offers`);

            // Mark all as loading
            setOfferFlightDetails(prev => {
                const updated = { ...prev };
                offersNeedingFlights.forEach(offer => {
                    updated[offer.id] = { loading: true };
                });
                return updated;
            });

            // Fetch sequentially with small delay to avoid API overload
            for (const offer of offersNeedingFlights) {
                // Check if aborted before each request
                if (abortController.signal.aborted) {
                    console.log('✋ Flight details fetch aborted (navigation to booking)');
                    break;
                }
                try {
                    const details = await getOfferDetails(offer.id, logApiCall, abortController.signal);
                    if (details) {
                        setOfferFlightDetails(prev => ({
                            ...prev,
                            [offer.id]: {
                                outbound: details.outboundFlight,
                                return: details.returnFlight,
                                outboundBrandedFares: details.outboundBrandedFares,
                                returnBrandedFares: details.returnBrandedFares,
                                loading: false
                            }
                        }));
                    } else {
                        setOfferFlightDetails(prev => ({ ...prev, [offer.id]: { loading: false } }));
                    }
                } catch (err: any) {
                    if (err?.name === 'AbortError') {
                        console.log('✋ Flight details fetch aborted (navigation to booking)');
                        break;
                    }
                    console.error(`Failed to fetch flight details for offer ${offer.id}:`, err);
                    setOfferFlightDetails(prev => ({ ...prev, [offer.id]: { loading: false } }));
                }
                // Small delay between requests
                await new Promise(resolve => setTimeout(resolve, 200));
            }
        };

        fetchAllFlightDetails();

        // Cleanup: abort on unmount (navigating away)
        return () => {
            abortController.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hotel?.offers]);

    // Fetch cached room data from HotelCache for enriched display
    useEffect(() => {
        if (!hotel) return;
        const hotelIdentifier = hotel.productId || hotel.id || id;
        if (!hotelIdentifier) return;

        fetch(`/api/hotels/${hotelIdentifier}/cache-details?lang=${language}`)
            .then(res => res.json())
            .then(data => {
                if (data.success && data.hasCache && data.rooms?.length > 0) {
                    console.log('[HotelDetail] ✅ Loaded', data.rooms.length, 'cached rooms from HotelCache');
                    setCachedRoomData({
                        hasCache: true,
                        rooms: data.rooms,
                        boardTypes: data.boardTypes || []
                    });
                }
            })
            .catch(err => console.warn('[HotelDetail] Cache details fetch error:', err));
    }, [hotel?.id, hotel?.productId, id, language]);


    const roomOptions = useMemo(() => {
        if (!hotel || !hotel.offers) return [];

        // Sort by price first
        const sortedOffers = [...hotel.offers].sort((a, b) => a.price - b.price);

        // Keep only the lowest priced offer for each room type
        const lowestByRoomType = new Map<string, Offer>();
        sortedOffers.forEach(offer => {
            const key = offer.roomType || offer.id; // Use roomType as key, fallback to id
            if (!lowestByRoomType.has(key)) {
                lowestByRoomType.set(key, offer);
            }
        });

        return Array.from(lowestByRoomType.values());
    }, [hotel]);



    // Calculate dates for display in pricing summary
    const checkInDate = searchQuery?.checkIn ? new Date(searchQuery.checkIn) : new Date();
    const nightsCount = searchQuery?.nights || 7;
    const checkOutDate = new Date(checkInDate);
    checkOutDate.setDate(checkInDate.getDate() + nightsCount);

    const formatDate = (date: Date) => date.toLocaleDateString(language === 'sv' ? 'sv-SE' : language === 'da' ? 'da-DK' : 'en-US', { day: 'numeric', month: 'short' });

    if (!hotel) return null;

    const lowestPrice = roomOptions.length > 0 ? roomOptions[0].price : 0;
    const lowestCurrency = roomOptions.length > 0 ? roomOptions[0].currency : 'EUR';

    // Handle Paired Flight Selection
    const handleSelectOutbound = (flight: RealFlightOption) => {
        setSelectedOutboundFlight(flight);
        // Auto-select matching inbound (same offer)
        const offerId = flight.id.split('-')[0];
        const matchingInbound = realFlights.inbound.find(f => f.id.startsWith(offerId));
        if (matchingInbound) {
            setSelectedInboundFlight(matchingInbound);
        }
    };

    const handleSelectInbound = (flight: RealFlightOption) => {
        setSelectedInboundFlight(flight);
        // Auto-select matching outbound (same offer)
        const offerId = flight.id.split('-')[0];
        const matchingOutbound = realFlights.outbound.find(f => f.id.startsWith(offerId));
        if (matchingOutbound) {
            setSelectedOutboundFlight(matchingOutbound);
        }
    };

    // Calculate total price with flight supplements + branded fare supplements
    // For hotel-only: no flight or branded fare supplements
    const flightSupplement = isPackageTour ? (selectedOutboundFlight?.priceDifference || 0) + (selectedInboundFlight?.priceDifference || 0) : 0;
    const brandedFareSupplement = isPackageTour ? (selectedOutboundBrandedFare?.priceDifference || 0) + (selectedReturnBrandedFare?.priceDifference || 0) : 0;
    const displayedTotalPrice = lowestPrice + flightSupplement + brandedFareSupplement;

    // Get branded fares from first offer that has them
    const firstOfferWithBrandedFares = Object.values(offerFlightDetails).find(d => (d.outboundBrandedFares && d.outboundBrandedFares.length > 0) || (d.returnBrandedFares && d.returnBrandedFares.length > 0));
    const availableOutboundBrandedFares = firstOfferWithBrandedFares?.outboundBrandedFares || [];
    const availableReturnBrandedFares = firstOfferWithBrandedFares?.returnBrandedFares || [];
    const hasBrandedFares = availableOutboundBrandedFares.length > 0 || availableReturnBrandedFares.length > 0;

    return (
        <div className="bg-gray-50 dark:bg-gray-950 min-h-screen font-sans text-gray-900 dark:text-gray-100 pb-24 md:pb-0 transition-colors duration-300">

            {/* IMMERSIVE HERO SECTION */}
            <div className="relative w-full h-[55vh] md:h-[70vh] min-h-[350px] md:min-h-[500px] group overflow-hidden bg-gray-900">
                <div className="absolute inset-0 bg-black/80 backdrop-blur-sm z-0"></div>
                <img
                    src={getHeroImageUrl(activeImage)}
                    alt={hotel.name}
                    className="relative w-full h-full object-cover z-10 transition-transform duration-[2s] hover:scale-110"
                />

                {/* Dark Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>

                {/* Back Button */}
                <button
                    onClick={() => onClose ? onClose() : navigate(-1)}
                    className="absolute top-8 left-8 z-30 w-12 h-12 flex items-center justify-center bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-full text-white transition-all border border-white/10 shadow-lg"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                </button>

                {/* Gallery Button - Updated Position for Mobile */}
                <button
                    onClick={() => handleOpenLightbox(0)}
                    className="absolute top-28 right-4 md:top-auto md:bottom-8 md:right-8 z-30 bg-black/60 hover:bg-black/80 backdrop-blur-md text-white px-4 py-2 md:px-5 md:py-3 rounded-xl border border-white/20 font-bold text-[10px] md:text-xs uppercase tracking-widest flex items-center gap-2 md:gap-3 transition-all transform hover:scale-105 shadow-xl"
                >

                    <div className="flex -space-x-2">
                        {galleryImages.slice(0, 3).map((img, i) => (
                            <img key={i} src={getThumbnailUrl(img, 60, 60)} className="w-5 h-5 md:w-6 md:h-6 rounded-full border border-white object-cover" alt="" />
                        ))}
                    </div>
                    <span>+{galleryImages.length} Photos</span>
                </button>

                {/* Hotel Content Overlay (Bottom Left) - Moved UP to allow overlap space */}
                <div className="absolute bottom-32 left-0 w-full px-8 md:px-16 z-20">
                    <div className="container mx-auto">
                        <div className="max-w-4xl animate-fade-in-up">

                            {/* NEW RATING DESIGN - More Prominent Stars */}
                            <div className="flex flex-col items-start gap-3 mb-6">
                                {/* Location Tag */}
                                <div className="inline-flex items-center gap-2 bg-black/30 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 text-white/90 text-sm font-medium">
                                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    {hotel.location?.city || ''}, {hotel.location?.country || ''}
                                </div>
                                {hotel.instagramUrl && (
                                    <a href={hotel.instagramUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 text-white text-sm font-bold shadow-lg hover:scale-105 transition-transform">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.468 2.527c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" /></svg>
                                        Instagram
                                    </a>
                                )}

                                {/* Data Source Badge removed per design — not relevant for end users */}

                                {/* Stars - Clear and Bright */}
                                <div className="flex items-center gap-2">
                                    <div className="flex gap-1">
                                        {[...Array(5)].map((_, i) => (
                                            <svg key={i} className={`w-6 h-6 ${i < Math.round(Number(hotel.rating || 0)) ? 'text-yellow-400 fill-current' : 'text-gray-400/50'}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke="none">
                                                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                                            </svg>
                                        ))}
                                    </div>
                                    {/* Numeric Badge for extra clarity */}
                                    <span className="bg-yellow-400 text-black text-xs font-bold px-2 py-0.5 rounded ml-2">
                                        {hotel.rating} {t('stars_label')}
                                    </span>
                                </div>
                            </div>

                            {/* Huge Title - Styled like screenshot */}
                            <h1 className="text-4xl md:text-6xl font-black text-white leading-tight tracking-tight shadow-black drop-shadow-2xl mb-6">
                                {hotel.name}
                            </h1>

                            {/* Booking Badge */}
                            {bookingData && (
                                <div className="inline-flex items-center gap-3 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg border border-blue-500/50 backdrop-blur-md">
                                    <span className="font-black text-xl">{bookingData.score.toFixed(1)}</span>
                                    <div className="flex flex-col leading-none">
                                        <span className="font-bold text-sm uppercase">{bookingData.wordRating}</span>
                                        <span className="text-[10px] opacity-80">{bookingData.reviewCount} Reviews</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* AlternativeDatesBar moved inside Hotel Overview */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-20 -mt-24 mb-12">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                    {/* LEFT COLUMN (2/3) */}
                    <div className="lg:col-span-2 space-y-12">

                        {/* 1. Vertical Categorized Overview - Replaces Horizontal Carousel */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                            <div className="flex items-center gap-4 mb-8 border-b border-gray-100 dark:border-gray-700 pb-4">
                                <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h2 className="text-3xl font-black text-gray-900 dark:text-white uppercase tracking-tight">
                                    {language === 'sv' ? 'HOTELLÖVERSIKT' : 'HOTEL OVERVIEW'}
                                </h2>
                            </div>

                            {/* Reviews Summary Card (New) */}
                            <ReviewSummaryCard bookingData={bookingData} />

                            {/* AlternativeDatesBar (Cheapest prices for alternative dates) placed where cache used to be */}
                            <div className="mt-6 mb-6 rounded-xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-700 relative">
                                <AlternativeDatesBar
                                    searchQuery={searchQuery || null}
                                    hotelId={hotel.id}
                                    language={language}
                                    isUpdating={isDateUpdating}
                                    onDateSelect={(newCheckIn, nights) => {
                                        if (!searchQuery) return;
                                        setIsDateUpdating(true);
                                        // Clear current offers so new search runs
                                        setHotel(prev => ({ ...prev, offers: [] }));
                                        setRealFlights({ outbound: [], inbound: [] });
                                        setSelectedOutboundFlight(null);
                                        setSelectedInboundFlight(null);
                                        setOfferFlightDetails({});
                                        // Update searchQuery with new date — triggers existing offers fetch effect
                                        setSearchQuery(prev => prev ? { ...prev, checkIn: newCheckIn, nights } : prev);
                                        setDateChangeCounter(c => c + 1);
                                    }}
                                />
                            </div>

                            {/* SanFlight Real Flight Selection — Only for Package Tours */}
                            {isPackageTour && (
                                <SanFlightSelection
                                    outboundFlights={realFlights.outbound}
                                    inboundFlights={realFlights.inbound}
                                    selectedOutbound={selectedOutboundFlight}
                                    selectedInbound={selectedInboundFlight}
                                    onSelectOutbound={handleSelectOutbound}
                                    onSelectInbound={handleSelectInbound}
                                    language={language}
                                    currency={currency}
                                />
                            )}

                            {/* Branded Fare Selection — shown after flight selection, Package Tours only */}
                            {isPackageTour && hasBrandedFares && (
                                <BrandedFareSelector
                                    outboundFares={availableOutboundBrandedFares}
                                    returnFares={availableReturnBrandedFares}
                                    outboundAirline={selectedOutboundFlight?.airline}
                                    returnAirline={selectedInboundFlight?.airline}
                                    outboundRoute={selectedOutboundFlight ? `${selectedOutboundFlight.departureAirport} → ${selectedOutboundFlight.arrivalAirport}` : undefined}
                                    returnRoute={selectedInboundFlight ? `${selectedInboundFlight.departureAirport} → ${selectedInboundFlight.arrivalAirport}` : undefined}
                                    selectedOutboundFare={selectedOutboundBrandedFare}
                                    selectedReturnFare={selectedReturnBrandedFare}
                                    onSelectOutboundFare={setSelectedOutboundBrandedFare}
                                    onSelectReturnFare={setSelectedReturnBrandedFare}
                                    language={language}
                                    currency={hotel?.currency || 'EUR'}
                                />
                            )}

                            {/* Vertical Scrolling Reviews (New) */}
                            <ReviewTicker />

                            {categorizedContent ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                                    <div className="md:col-span-2">
                                        <SectionWidget
                                            title={language === 'sv' ? 'Sammanfattning' : 'Summary'}
                                            icon={<GeneralIcon />}
                                            content={categorizedContent.intro}
                                            colorClass="bg-gray-50/50 border-gray-100 shadow-sm"
                                            darkColorClass="dark:bg-gray-900/50 dark:border-gray-700"
                                            textColorClass="text-gray-700"
                                        />
                                    </div>
                                    <SectionWidget
                                        title={language === 'sv' ? 'Mat & Dryck' : 'Dining'}
                                        icon={<DiningIcon />}
                                        content={categorizedContent.dining}
                                        colorClass="bg-orange-50/30 border-orange-100 shadow-sm"
                                        darkColorClass="dark:bg-orange-900/20 dark:border-orange-900/50"
                                        textColorClass="text-orange-800 dark:text-orange-300"
                                    />
                                    <SectionWidget
                                        title={language === 'sv' ? 'Aktiviteter' : 'Activities'}
                                        icon={<PoolIcon />}
                                        content={categorizedContent.activities}
                                        colorClass="bg-sky-50/30 border-sky-100 shadow-sm"
                                        darkColorClass="dark:bg-sky-900/20 dark:border-sky-900/50"
                                        textColorClass="text-sky-800 dark:text-sky-300"
                                    />
                                    <SectionWidget
                                        title={language === 'sv' ? 'Rum & Boende' : 'Rooms & Accommodation'}
                                        icon={<RoomIcon />}
                                        content={categorizedContent.rooms}
                                        colorClass="bg-indigo-50/30 border-indigo-100 shadow-sm"
                                        darkColorClass="dark:bg-indigo-900/20 dark:border-indigo-900/50"
                                        textColorClass="text-indigo-800 dark:text-indigo-300"
                                    />
                                    <SectionWidget
                                        title={language === 'sv' ? 'Läge & Omgivning' : 'Location & Area'}
                                        icon={<LocationIcon />}
                                        content={categorizedContent.location}
                                        colorClass="bg-emerald-50/30 border-emerald-100 shadow-sm"
                                        darkColorClass="dark:bg-emerald-900/20 dark:border-emerald-900/50"
                                        textColorClass="text-emerald-800 dark:text-emerald-300"
                                    />
                                    <SectionWidget
                                        title={language === 'sv' ? 'Familj & Barn' : 'Family & Kids'}
                                        icon={<FamilyIcon />}
                                        content={categorizedContent.family}
                                        colorClass="bg-yellow-50/30 border-yellow-100 shadow-sm"
                                        darkColorClass="dark:bg-yellow-900/20 dark:border-yellow-900/50"
                                        textColorClass="text-yellow-800 dark:text-yellow-300"
                                    />
                                    <SectionWidget
                                        title={language === 'sv' ? 'Wellness & Spa' : 'Wellness & Spa'}
                                        icon={<WellnessIcon />}
                                        content={categorizedContent.wellness}
                                        colorClass="bg-pink-50/30 border-pink-100 shadow-sm"
                                        darkColorClass="dark:bg-pink-900/20 dark:border-pink-900/50"
                                        textColorClass="text-pink-800 dark:text-pink-300"
                                    />
                                    {categorizedContent.other && (
                                        <SectionWidget
                                            title={language === 'sv' ? 'Övrig Information' : 'Other Information'}
                                            icon={<GeneralIcon />}
                                            content={categorizedContent.other}
                                            colorClass="bg-slate-50/30 border-slate-100 shadow-sm"
                                            darkColorClass="dark:bg-slate-900/20 dark:border-slate-900/50"
                                            textColorClass="text-slate-800 dark:text-slate-300"
                                        />
                                    )}
                                </div>
                            ) : (
                                <div className="animate-pulse space-y-4">
                                    <div className="h-32 bg-gray-100 dark:bg-gray-700 rounded-2xl w-full"></div>
                                    <div className="h-32 bg-gray-100 dark:bg-gray-700 rounded-2xl w-full"></div>
                                </div>
                            )}
                        </div>

                        {/* Amenities Grid */}
                        <HotelAmenities
                            facilities={details?.facilities && details.facilities.length > 0 ? details.facilities : hotel.facilities}
                            language={language}
                        />

                        {/* Flight Selection Section - Only show for Package Tours when real flights are available */}
                        {isPackageTour && (realFlights.outbound.length > 0 || realFlights.inbound.length > 0) && (
                            <SanFlightSelection
                                outboundFlights={realFlights.outbound}
                                inboundFlights={realFlights.inbound}
                                selectedOutbound={selectedOutboundFlight}
                                selectedInbound={selectedInboundFlight}
                                onSelectOutbound={handleSelectOutbound}
                                onSelectInbound={handleSelectInbound}
                                language={language}
                                currency={currency}
                            />
                        )}

                        {/* Branded Fare Selection — Package Tours only */}
                        {isPackageTour && hasBrandedFares && (
                            <BrandedFareSelector
                                outboundFares={availableOutboundBrandedFares}
                                returnFares={availableReturnBrandedFares}
                                outboundAirline={selectedOutboundFlight?.airline}
                                returnAirline={selectedInboundFlight?.airline}
                                outboundRoute={selectedOutboundFlight ? `${selectedOutboundFlight.departureAirport} → ${selectedOutboundFlight.arrivalAirport}` : undefined}
                                returnRoute={selectedInboundFlight ? `${selectedInboundFlight.departureAirport} → ${selectedInboundFlight.arrivalAirport}` : undefined}
                                selectedOutboundFare={selectedOutboundBrandedFare}
                                selectedReturnFare={selectedReturnBrandedFare}
                                onSelectOutboundFare={setSelectedOutboundBrandedFare}
                                onSelectReturnFare={setSelectedReturnBrandedFare}
                                language={language}
                                currency={hotel?.currency || 'EUR'}
                            />
                        )}

                        {/* Loading state for flights — Package Tours only */}
                        {isPackageTour && realFlightsLoading && (
                            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 mb-8">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center animate-pulse">
                                        <span className="text-xl">✈️</span>
                                    </div>
                                    <div>
                                        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse w-40 mb-2"></div>
                                        <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded animate-pulse w-56"></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 2. Available Rooms List (Expanded) */}
                        <div id="rooms-section">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{language === 'sv' ? 'Tillgängliga Rum' : 'Available Room Options'} <span className="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 text-sm font-normal px-2 py-1 rounded-md ml-2">{roomOptions.length} Options Found</span></h2>

                            <div className="space-y-4">
                                {roomOptions.map((offer, idx) => {
                                    const isExpanded = expandedRoomId === offer.id;
                                    const details = roomDetails[offer.id];
                                    // Use scraper status for specific loading indicator
                                    const isLoadingDetails = isExpanded && !details && isScrapingRooms;

                                    // ── Match cached room from HotelCache (admin-curated, top priority) ──
                                    const matchedCachedRoom = cachedRoomData.hasCache
                                        ? cachedRoomData.rooms.find(cr => {
                                            const clean = (s: string) => s?.toLowerCase().replace(/[^a-z0-9 ]/g, '').trim() || '';
                                            const offerClean = clean(offer.roomType);
                                            const cacheClean = clean(cr.roomTypeName);
                                            // Exact match or substantial keyword overlap
                                            if (offerClean === cacheClean) return true;
                                            const offerTerms = offerClean.split(' ').filter(t => t.length > 2);
                                            const cacheTerms = cacheClean.split(' ').filter(t => t.length > 2);
                                            const overlap = offerTerms.filter(t => cacheTerms.some(ct => ct.includes(t) || t.includes(ct))).length;
                                            return overlap >= Math.max(1, Math.min(offerTerms.length, cacheTerms.length) * 0.6);
                                        })
                                        : undefined;

                                    // Cached room image takes priority over Google/scraped
                                    const cachedRoomImage = matchedCachedRoom?.images?.[0] || null;
                                    const cachedRoomDescription = matchedCachedRoom?.description || '';
                                    const cachedRoomHighlights = matchedCachedRoom?.highlights || [];

                                    // Match Google room images using Fuzzy Matching logic (fallback)
                                    const matchedGoogleRoom = !cachedRoomImage ? googleDetails?.rooms?.map(gr => {
                                        const clean = (s: string) => s?.toLowerCase().replace(/[^a-z0-9 ]/g, '') || '';
                                        const s1 = clean(offer.roomType);
                                        const s2 = clean(gr.name);
                                        const terms1 = s1.split(' ').filter(t => t.length > 2);
                                        const terms2 = s2.split(' ').filter(t => t.length > 2);

                                        // 1. Intersection Count
                                        let score = terms1.filter(t => terms2.some(t2 => t2.includes(t) || t.includes(t2))).length * 1.5;

                                        // 2. View/Feature Analysis
                                        const views = ['sea', 'garden', 'pool', 'land', 'mountain', 'ocean', 'city'];
                                        const s1Views = views.filter(v => s1.includes(v));
                                        const s2Views = views.filter(v => s2.includes(v));

                                        // Bonus for matching views
                                        const commonViews = s1Views.filter(v => s2Views.includes(v));
                                        score += commonViews.length * 3;

                                        // Penalty for CONFLICTING views (e.g. Sea vs Garden)
                                        if (s1Views.length > 0 && s2Views.length > 0 && commonViews.length === 0) {
                                            score -= 5;
                                        }

                                        return { room: gr, score };
                                    })
                                        .sort((a, b) => b.score - a.score)
                                        .find(r => r.score > 2)?.room : undefined;

                                    // Priority: cached > Google > null
                                    const roomImage = cachedRoomImage || matchedGoogleRoom?.images?.[0] || null;

                                    return (
                                        <div key={idx} className={`bg-white dark:bg-gray-800 rounded-2xl border transition-all duration-300 ${isExpanded ? 'border-blue-300 dark:border-blue-700 shadow-xl ring-1 ring-blue-100 dark:ring-blue-900' : 'border-gray-200 dark:border-gray-700 hover:shadow-lg'}`}>
                                            <div className="p-6 flex flex-col md:flex-row gap-6">
                                                {/* Room Image Thumbnail — Priority: HotelCache > Google */}
                                                {roomImage && (
                                                    <div className="w-full md:w-32 h-24 md:h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                                                        <img
                                                            src={roomImage}
                                                            alt={offer.roomType}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex-1">
                                                    <div className="flex items-start justify-between">
                                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{offer.roomType}</h3>
                                                        {isPackageTour && (
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() => {
                                                                        setFlightOptionsOfferId(offer.id);
                                                                        setShowFlightOptionsModal(true);
                                                                    }}
                                                                    className="text-xs font-bold text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/30 px-3 py-1.5 rounded-full hover:bg-cyan-100 dark:hover:bg-cyan-900/50 transition-colors flex items-center gap-1"
                                                                >
                                                                    ✈️ {t('alternative_flights')}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex flex-wrap gap-2 mb-4">
                                                        <span className="px-3 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs font-bold rounded uppercase tracking-wider">
                                                            {offer.board || 'All Inclusive'}
                                                        </span>
                                                    </div>
                                                    {isPackageTour && (
                                                        <FlightDetailsCompact
                                                            language={language}
                                                            onShowModal={(out, ret) => {
                                                                setSelectedFlightForModal({ outbound: out, return: ret });
                                                                setShowFlightDetailsModal(true);
                                                            }}
                                                            offer={offer}
                                                            lazyFlightData={offerFlightDetails[offer.id]}
                                                            realOutbound={selectedOutboundFlight}
                                                            realInbound={selectedInboundFlight}
                                                            onShowAlternativeFlights={(hotel as HolidayPackage)?.searchId ? () => handleFetchAlternativeFlights((hotel as HolidayPackage).searchId!, offer) : undefined}
                                                            onChangeFlight={() => {
                                                                // Save hotel and searchQuery to sessionStorage for retrieval when returning
                                                                sessionStorage.setItem('flightSelection_hotel', JSON.stringify(hotel));
                                                                sessionStorage.setItem('flightSelection_searchQuery', JSON.stringify(searchQuery));

                                                                // Navigate to FlightSelection page instead of modal
                                                                const params = new URLSearchParams();
                                                                params.set('offerId', offer.id);
                                                                params.set('hotelId', hotel.id);
                                                                params.set('departureId', searchQuery?.departureId || '');
                                                                params.set('arrivalId', searchQuery?.destinationId || '');
                                                                params.set('checkIn', searchQuery?.checkIn || '');
                                                                params.set('nights', String(searchQuery?.nights || 7));
                                                                params.set('adults', String(searchQuery?.adults || 2));
                                                                params.set('children', String(searchQuery?.children || 0));
                                                                if (searchQuery?.childrenAges?.length) {
                                                                    params.set('childrenAges', searchQuery.childrenAges.join(','));
                                                                }
                                                                params.set('hotelName', hotel.name);
                                                                params.set('roomType', offer.roomType || '');
                                                                params.set('totalPrice', String(offer.price));

                                                                navigate(`/flight-selection?${params.toString()}`);
                                                            }}
                                                        />
                                                    )}


                                                </div>

                                                <div className="flex flex-col items-end justify-center min-w-[200px] border-t md:border-t-0 md:border-l border-gray-100 dark:border-gray-700 pt-4 md:pt-0 md:pl-6">
                                                    <div className="text-right mb-3">
                                                        <span className="block text-2xl font-black text-gray-900 dark:text-white">{Math.round(offer.price).toLocaleString('sv-SE')} {offer.currency}</span>
                                                        <span className="text-xs text-gray-400">{t('total_price_label')}</span>
                                                    </div>
                                                    <button
                                                        onClick={() => handleBook(offer)}
                                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-xl transition-colors shadow-md hover:shadow-lg"
                                                    >
                                                        {t('select_room')}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Expanded Content */}
                                            {isExpanded && (
                                                <div className="border-t border-blue-100 dark:border-blue-900 bg-blue-50/30 dark:bg-blue-900/10 p-6 animate-fade-in">
                                                    {/* Cached Room Description from HotelCache (admin-curated, top priority) */}
                                                    {cachedRoomDescription && (
                                                        <div className="mb-4">
                                                            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{cachedRoomDescription}</p>
                                                            {cachedRoomHighlights.length > 0 && (
                                                                <div className="flex flex-wrap gap-2 mt-3">
                                                                    {cachedRoomHighlights.map((h, hIdx) => (
                                                                        <span key={hIdx} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full font-medium">✓ {h}</span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {matchedCachedRoom && matchedCachedRoom.images.length > 1 && (
                                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                                                                    {matchedCachedRoom.images.slice(0, 4).map((img, imgIdx) => (
                                                                        <img
                                                                            key={imgIdx}
                                                                            src={img}
                                                                            alt=""
                                                                            className="w-full h-32 object-cover rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                                                                            onClick={() => window.open(img, '_blank')}
                                                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {isLoadingDetails ? (
                                                        <div className="flex items-center justify-center py-8 text-blue-600 dark:text-blue-400 gap-3">
                                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                                            <span className="text-sm font-medium">{t('loading_room_details')}</span>
                                                        </div>
                                                    ) : details ? (
                                                        <div className="space-y-4">
                                                            <div className="flex justify-between items-start">
                                                                <div>
                                                                    <h4 className="text-sm font-bold text-gray-800 dark:text-white mb-1">{details.name}</h4>
                                                                    <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed max-w-2xl">{details.description}</p>
                                                                    <div className="flex gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                                                                        {details.size && <span>{t('room_size')}: <b>{details.size}</b></span>}
                                                                        {details.bedType && <span>{t('bed_type')}: <b>{details.bedType}</b></span>}
                                                                    </div>
                                                                </div>
                                                                <span className="text-[10px] bg-blue-100 text-blue-800 px-2 py-1 rounded font-bold">{t('booking_verified')}</span>
                                                            </div>

                                                            {details.photos && details.photos.length > 0 && (
                                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                                                    {details.photos.slice(0, 4).map((photo, pIdx) => (
                                                                        <img
                                                                            key={pIdx}
                                                                            src={photo}
                                                                            alt=""
                                                                            className="w-full h-32 object-cover rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                                                                            onClick={() => window.open(photo, '_blank')}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-xs italic">
                                                            {t('no_room_details')}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                    </div>

                    {/* RIGHT COLUMN (1/3) - No Sticky */}
                    <div className="lg:col-span-1 space-y-8">

                        {/* 2. Dark Pricing Summary */}
                        <div className="relative">
                            <div className="bg-[#0F172A] rounded-3xl shadow-2xl p-8 text-white relative overflow-hidden">
                                {/* Decorative BG */}
                                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl -mr-10 -mt-10"></div>

                                <div className="relative z-10">
                                    <p className="text-xs font-bold text-blue-200 uppercase tracking-widest mb-1">{t('total_price_for_nights').replace('{0}', String(searchQuery?.nights || 7))}</p>

                                    {/* Detailed Booking Info Block */}
                                    <div className="mb-4 pt-2 border-t border-gray-700/50">
                                        <div className="flex justify-between text-xs text-gray-300 mb-1">
                                            <span>{t('check_in')}</span>
                                            <span className="font-bold text-white">{formatDate(checkInDate)}</span>
                                        </div>
                                        <div className="flex justify-between text-xs text-gray-300 mb-1">
                                            <span>{t('check_out')}</span>
                                            <span className="font-bold text-white">{formatDate(checkOutDate)}</span>
                                        </div>
                                        <div className="flex justify-between text-xs text-gray-300 mb-1">
                                            <span>{t('duration_label')}</span>
                                            <span className="font-bold text-white">{t('nights_count').replace('{0}', String(nightsCount))}</span>
                                        </div>
                                        <div className="flex justify-between text-xs text-gray-300">
                                            <span>{t('guests_label')}</span>
                                            <span className="font-bold text-white">
                                                {t('adults_count').replace('{0}', String(searchQuery?.adults || 2))}, {t('children_count').replace('{0}', String(searchQuery?.children || 0))}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-baseline gap-2 mb-2">
                                        <span className="text-4xl font-black text-white">{Math.round(displayedTotalPrice).toLocaleString('sv-SE')}</span>
                                        <span className="text-xl font-bold text-gray-400">{lowestCurrency}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-gray-400 mb-6">
                                        <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                        {isPackageTour
                                            ? t('package_includes_desc')
                                            : (language === 'sv' ? 'Inkluderar: Hotell & Skatter' : 'Includes: Hotel & Taxes')
                                        }
                                    </div>

                                    {/* Selection Box */}
                                    <div className="border border-dashed border-gray-600 rounded-xl p-4 mb-6 bg-white/5">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">{t('selected_room_label')}</p>
                                        <p className="text-sm italic text-gray-300">{t('select_room_hint')}</p>
                                    </div>

                                    <button
                                        onClick={() => document.getElementById('rooms-section')?.scrollIntoView({ behavior: 'smooth' })}
                                        className="w-full bg-gray-500 hover:bg-gray-400 text-white font-bold py-4 rounded-xl shadow-lg transition-colors text-sm uppercase tracking-wide"
                                    >
                                        {t('select_room_btn')}
                                    </button>

                                    {/* Instagram Button */}
                                    {instagramUrl && (
                                        <a
                                            href={instagramUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-4 w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-3 rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-105 text-sm"
                                        >
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.468.99c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" /></svg>
                                            {t('follow_on_instagram')}
                                        </a>
                                    )}

                                    <div className="mt-4 text-center">
                                        <p className="text-[10px] text-gray-500 flex items-center justify-center gap-1">
                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                            {t('secure_booking_msg')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 2. Blue Gradient Weather Widget */}
                        {details?.coordinates && (
                            <WeatherWidget
                                lat={String(details.coordinates.latitude)}
                                lon={String(details.coordinates.longitude)}
                                checkIn={searchQuery?.checkIn || new Date().toISOString().split('T')[0]}
                                language={language}
                            />
                        )}

                        {/* 3. Nearby Attractions (Updated) */}
                        <NearbyAttractions city={hotel.location?.city || ''} language={language} />

                        {/* 4. Location Map Card */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wider mb-4 flex items-center gap-2">
                                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                {t('location_map') || (language === 'sv' ? 'KARTA & LÄGE' : 'LOCATION & MAP')}
                            </h3>
                            {details?.coordinates ? (
                                <div className="h-64 w-full bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden relative">
                                    <iframe width="100%" height="100%" frameBorder="0" style={{ border: 0 }} src={`https://maps.google.com/maps?q=${details.coordinates.latitude},${details.coordinates.longitude}&z=14&output=embed`} allowFullScreen></iframe>
                                </div>
                            ) : (
                                <div className="h-64 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center text-gray-400 text-sm">{t('map_unavailable')}</div>
                            )}
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">{hotel.location?.city || ''}, Turkey</p>
                        </div>

                        {/* 5. Sidebar Gallery (Moved to Bottom) */}
                        <SidebarGallery images={galleryImages} onClick={handleOpenLightbox} />

                    </div>
                </div>
            </div>

            {/* STICKY BOTTOM BAR (Updated) */}
            <StickyBookingBar
                hotel={hotel}
                lowestPrice={lowestPrice}
                currency={lowestCurrency}
                language={language}
                searchQuery={searchQuery}
                onBook={() => document.getElementById('rooms-section')?.scrollIntoView({ behavior: 'smooth' })}
                visible={showStickyBar}
                selectedOutbound={selectedOutboundFlight}
                selectedInbound={selectedInboundFlight}
            />

            {/* Lightbox */}
            {isLightboxOpen && (
                <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center backdrop-blur-sm">
                    <button onClick={handleCloseLightbox} className="absolute top-6 right-6 text-white/70 hover:text-white p-2 z-50"><svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                    <button onClick={(e) => { e.stopPropagation(); handlePrevImage(); }} className="absolute left-4 md:left-8 text-white/50 hover:text-white p-4 transition-colors z-50"><svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 md:h-16 md:w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" /></svg></button>
                    <div className="relative w-full h-full flex items-center justify-center p-4 md:p-10" onClick={handleCloseLightbox}><img src={galleryImages[lightboxIndex]} alt={`Gallery ${lightboxIndex + 1}`} className="max-w-full max-h-full object-contain shadow-2xl rounded-sm select-none" onClick={(e) => e.stopPropagation()} /><div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-white/80 font-mono text-sm bg-black/50 px-4 py-1 rounded-full">{lightboxIndex + 1} / {galleryImages.length}</div></div>
                    <button onClick={(e) => { e.stopPropagation(); handleNextImage(); }} className="absolute right-4 md:right-8 text-white/50 hover:text-white p-4 transition-colors z-50"><svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 md:h-16 md:w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" /></svg></button>
                </div>
            )}

            {/* Alternative Flights Modal — Package Tours only */}
            {isPackageTour && showFlightModal && selectedOfferForFlight && (
                <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center backdrop-blur-sm p-4" onClick={() => setShowFlightModal(false)}>
                    <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-5 flex items-center justify-between rounded-t-2xl z-10">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                    <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                        ✈️ {t('alternative_flights_title')}
                                    </h3>
                                    <p className="text-sm text-white/70">
                                        {selectedOfferForFlight.roomType} • {selectedOfferForFlight.board}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowFlightModal(false)}
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Current Selection */}
                        <div className="px-6 py-4 bg-blue-50 dark:bg-blue-900/20 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/40 px-2 py-1 rounded-full">
                                    {t('current_selection_label')}
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {/* Current Outbound */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs font-bold text-emerald-600">✈️ {t('outbound_flight')}</span>
                                    </div>
                                    <div className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                                        {offerFlightDetails[selectedOfferForFlight.id]?.outbound?.airline || 'Charter'}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {offerFlightDetails[selectedOfferForFlight.id]?.outbound?.departureTime} {offerFlightDetails[selectedOfferForFlight.id]?.outbound?.departureAirport} → {offerFlightDetails[selectedOfferForFlight.id]?.outbound?.arrivalTime} {offerFlightDetails[selectedOfferForFlight.id]?.outbound?.arrivalAirport}
                                    </div>
                                </div>
                                {/* Current Return */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs font-bold text-orange-600">✈️ {t('return_flight')}</span>
                                    </div>
                                    <div className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                                        {offerFlightDetails[selectedOfferForFlight.id]?.return?.airline || 'Charter'}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {offerFlightDetails[selectedOfferForFlight.id]?.return?.departureTime} {offerFlightDetails[selectedOfferForFlight.id]?.return?.departureAirport} → {offerFlightDetails[selectedOfferForFlight.id]?.return?.arrivalTime} {offerFlightDetails[selectedOfferForFlight.id]?.return?.arrivalAirport}
                                    </div>
                                </div>
                            </div>
                            <div className="text-right mt-2">
                                <span className="text-lg font-bold text-gray-900 dark:text-white">
                                    {Math.round(selectedOfferForFlight.price).toLocaleString('sv-SE')} {selectedOfferForFlight.currency}
                                </span>
                            </div>
                        </div>

                        {/* Alternative Options */}
                        <div className="px-6 py-4">
                            {alternativeOffers.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <div className="w-12 h-12 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                                    <p className="font-medium">{t('searching_alternative_flights')}</p>
                                    <p className="text-xs mt-1 text-gray-400">{t('try_another_room')}</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                        {t('alternatives_found_label').replace('{0}', String(alternativeOffers.length))}
                                    </p>
                                    {alternativeOffers.map((altOffer) => {
                                        const priceDiff = altOffer.price - selectedOfferForFlight.price;
                                        const outbound = altOffer.outboundFlight || offerFlightDetails[altOffer.id]?.outbound;
                                        const returnFl = altOffer.returnFlight || offerFlightDetails[altOffer.id]?.return;

                                        return (
                                            <div
                                                key={altOffer.id}
                                                className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden hover:border-blue-300 dark:hover:border-blue-600 transition-all hover:shadow-md"
                                            >
                                                {/* Flight legs */}
                                                <div className="p-4">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        {/* Outbound Leg */}
                                                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <span className="text-xs font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-full">
                                                                    ✈️ {t('outbound_flight')}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <img
                                                                    src={outbound?.logoUrl || `https://images.kiwi.com/airlines/64/XX.png`}
                                                                    className="w-8 h-8 object-contain rounded border border-gray-200 bg-white p-0.5"
                                                                    alt={outbound?.airline || ''}
                                                                    onError={(e) => { (e.target as HTMLImageElement).src = 'https://cdn-icons-png.flaticon.com/512/723/723955.png'; }}
                                                                />
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                                        {outbound?.airline || 'Charter Flight'}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                        {outbound?.departureTime} {outbound?.departureAirport} → {outbound?.arrivalTime} {outbound?.arrivalAirport}
                                                                    </div>
                                                                    {outbound?.flightDate && (
                                                                        <div className="text-xs text-gray-400 mt-0.5">{outbound.flightDate}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Return Leg */}
                                                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <span className="text-xs font-bold text-orange-600 bg-orange-50 dark:bg-orange-900/30 px-2 py-0.5 rounded-full">
                                                                    ✈️ {t('return_flight')}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <img
                                                                    src={returnFl?.logoUrl || `https://images.kiwi.com/airlines/64/XX.png`}
                                                                    className="w-8 h-8 object-contain rounded border border-gray-200 bg-white p-0.5"
                                                                    alt={returnFl?.airline || ''}
                                                                    onError={(e) => { (e.target as HTMLImageElement).src = 'https://cdn-icons-png.flaticon.com/512/723/723955.png'; }}
                                                                />
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                                        {returnFl?.airline || 'Charter Flight'}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                        {returnFl?.departureTime} {returnFl?.departureAirport} → {returnFl?.arrivalTime} {returnFl?.arrivalAirport}
                                                                    </div>
                                                                    {returnFl?.flightDate && (
                                                                        <div className="text-xs text-gray-400 mt-0.5">{returnFl.flightDate}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Selection Footer */}
                                                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-lg font-bold text-gray-900 dark:text-white">
                                                            {altOffer.price ? `${Math.round(altOffer.price).toLocaleString('sv-SE')} ${altOffer.currency}` : ''}
                                                        </span>
                                                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${priceDiff > 0 ? 'bg-red-50 text-red-600' : priceDiff < 0 ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                                                            {priceDiff > 0 ? `+${Math.round(priceDiff).toLocaleString('sv-SE')}` : priceDiff < 0 ? Math.round(priceDiff).toLocaleString('sv-SE') : '−'}
                                                        </span>
                                                    </div>
                                                    <button
                                                        className="px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-bold rounded-lg hover:shadow-lg transition-all flex items-center gap-1.5"
                                                        onClick={() => {
                                                            setShowFlightModal(false);
                                                            setExpandedRoomId(altOffer.id);
                                                        }}
                                                    >
                                                        ✓ {t('select_this_flight')}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Flight Details Modal (GetOfferDetails) — Package Tours only */}
            {isPackageTour && showFlightDetailsModal && selectedFlightForModal && (
                <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center backdrop-blur-sm p-4" onClick={() => setShowFlightDetailsModal(false)}>
                    <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-5 flex items-center justify-between rounded-t-2xl">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                    <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">
                                        {t('flight_details_title')}
                                    </h3>
                                    <p className="text-sm text-white/70">
                                        {t('complete_journey_info')}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowFlightDetailsModal(false)}
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Flight Details Content */}
                        <div className="p-6 space-y-6">
                            {/* Helper to render a flight leg (outbound or return) */}
                            {(() => {
                                const renderFlightCard = (flight: any, type: 'outbound' | 'return') => {
                                    if (!flight) return null;
                                    const isOutbound = type === 'outbound';
                                    const colorFrom = isOutbound ? 'from-sky-50' : 'from-indigo-50';
                                    const colorTo = isOutbound ? 'to-blue-50' : 'to-purple-50';
                                    const borderColor = isOutbound ? 'border-sky-100' : 'border-indigo-100';
                                    const accentColor = isOutbound ? 'text-blue-600' : 'text-indigo-600';
                                    const accentBg = isOutbound ? 'bg-blue-100 text-blue-700' : 'bg-indigo-100 text-indigo-700';
                                    const lineGradient = isOutbound ? 'from-blue-400 to-indigo-500' : 'from-indigo-400 to-purple-500';
                                    const icon = isOutbound ? '🛫' : '🛬';
                                    const label = isOutbound ? t('outbound_flight_label') : t('return_flight_label');

                                    const flightDate = flight.flightDate || '';
                                    const hasSegments = flight.segments && flight.segments.length > 1;

                                    // Format ISO datetime to HH:mm
                                    const formatFlightTime = (raw: string): string => {
                                        if (!raw) return '';
                                        const str = String(raw);
                                        if (str.includes('T')) {
                                            const timePart = str.split('T')[1];
                                            if (timePart) return timePart.substring(0, 5);
                                        }
                                        const match = str.match(/(\d{1,2}:\d{2})/);
                                        if (match) return match[1];
                                        return str;
                                    };

                                    // Format ISO date to readable format (e.g., "24 Jun 2026")
                                    const formatFlightDate = (raw: string): string => {
                                        if (!raw) return '';
                                        const str = String(raw);
                                        try {
                                            const d = new Date(str);
                                            if (isNaN(d.getTime())) return str;
                                            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                            return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
                                        } catch {
                                            return str;
                                        }
                                    };

                                    // Format layover duration
                                    const formatLayover = (mins: number) => {
                                        if (!mins || mins <= 0) return '';
                                        const h = Math.floor(mins / 60);
                                        const m = mins % 60;
                                        return h > 0 ? `${h}h ${m}m` : `${m}m`;
                                    };

                                    return (
                                        <div className={`bg-gradient-to-br ${colorFrom} ${colorTo} dark:from-gray-800 dark:to-gray-800 rounded-xl p-5 border ${borderColor} dark:border-gray-700`}>
                                            <div className="flex items-center gap-2 mb-4">
                                                <span className="text-2xl">{icon}</span>
                                                <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                                                    {label}
                                                </h4>
                                                {flightDate && (
                                                    <span className={`ml-auto text-sm font-medium ${accentBg} dark:bg-opacity-30 px-3 py-1 rounded-full`}>
                                                        {formatFlightDate(flightDate)}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Overall flight summary */}
                                            <div className="flex items-center gap-6 mb-4">
                                                {/* Airline Logo */}
                                                <div className="flex flex-col items-center">
                                                    <img
                                                        src={flight.logoUrl || `https://images.kiwi.com/airlines/64/${(flight.airline || '').substring(0, 2)}.png`}
                                                        className="w-14 h-14 object-contain rounded-lg border border-gray-200 dark:border-gray-600 bg-white p-2"
                                                        alt={flight.airline}
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).src = 'https://cdn-icons-png.flaticon.com/512/723/723955.png';
                                                        }}
                                                    />
                                                    <span className="text-xs font-bold text-gray-600 dark:text-gray-400 mt-1">
                                                        {flight.flightNumber}
                                                    </span>
                                                </div>

                                                {/* Flight Times */}
                                                <div className="flex-1">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="text-center">
                                                            <div className="text-3xl font-black text-gray-900 dark:text-white">
                                                                {formatFlightTime(flight.departureTime)}
                                                            </div>
                                                            <div className={`text-sm font-bold ${accentColor} dark:opacity-80`}>
                                                                {flight.departureAirport}
                                                            </div>
                                                        </div>

                                                        <div className="flex-1 mx-4 relative">
                                                            <div className={`h-0.5 bg-gradient-to-r ${lineGradient}`}></div>
                                                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 px-3 py-1 rounded-full text-xs font-bold text-gray-500 border border-gray-200 dark:border-gray-600">
                                                                {formatDuration(flight.duration)}
                                                            </div>
                                                        </div>

                                                        <div className="text-center">
                                                            <div className="text-3xl font-black text-gray-900 dark:text-white">
                                                                {formatFlightTime(flight.arrivalTime)}
                                                            </div>
                                                            <div className={`text-sm font-bold ${accentColor} dark:opacity-80`}>
                                                                {flight.arrivalAirport}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Tags */}
                                                    <div className="flex flex-wrap gap-2">
                                                        <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-3 py-1 rounded-full">
                                                            {flight.airline}
                                                        </span>
                                                        <span className={`text-xs px-3 py-1 rounded-full ${flight.stops === 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'}`}>
                                                            {flight.stops === 0 ? t('direct_flight') : `${flight.stops} ${t('stops')}`}
                                                        </span>
                                                        {flight.baggage && (
                                                            <span className="text-xs bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 px-3 py-1 rounded-full">
                                                                🧳 {flight.baggage}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Individual Segments (when multi-leg) */}
                                            {hasSegments && (
                                                <div className="mt-4 border-t border-gray-200 dark:border-gray-600 pt-4">
                                                    <h5 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                                                        {t('journey_details_label')}
                                                    </h5>
                                                    <div className="space-y-0">
                                                        {flight.segments.map((seg: any, idx: number) => (
                                                            <div key={idx}>
                                                                {/* Segment */}
                                                                <div className="flex items-center gap-3 py-2">
                                                                    {/* Timeline dot */}
                                                                    <div className="flex flex-col items-center w-6">
                                                                        <div className={`w-3 h-3 rounded-full ${isOutbound ? 'bg-blue-500' : 'bg-indigo-500'}`}></div>
                                                                        {idx < flight.segments.length - 1 && (
                                                                            <div className={`w-0.5 h-8 ${isOutbound ? 'bg-blue-200' : 'bg-indigo-200'}`}></div>
                                                                        )}
                                                                    </div>

                                                                    {/* Segment airline logo */}
                                                                    <img
                                                                        src={seg.logoUrl || `https://images.kiwi.com/airlines/64/${(seg.airline || '').substring(0, 2)}.png`}
                                                                        className="w-8 h-8 object-contain rounded border border-gray-200 bg-white p-1"
                                                                        alt={seg.airline}
                                                                        onError={(e) => {
                                                                            (e.target as HTMLImageElement).src = 'https://cdn-icons-png.flaticon.com/512/723/723955.png';
                                                                        }}
                                                                    />

                                                                    {/* Segment info */}
                                                                    <div className="flex-1 flex items-center justify-between">
                                                                        <div>
                                                                            <div className="flex items-center gap-2">
                                                                                <span className="text-sm font-bold text-gray-900 dark:text-white">
                                                                                    {formatFlightTime(seg.departureTime)}
                                                                                </span>
                                                                                <span className="text-xs text-gray-400">→</span>
                                                                                <span className="text-sm font-bold text-gray-900 dark:text-white">
                                                                                    {formatFlightTime(seg.arrivalTime)}
                                                                                </span>
                                                                            </div>
                                                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                                {seg.departureAirport || seg.departureAirportCode} → {seg.arrivalAirport || seg.arrivalAirportCode}
                                                                            </div>
                                                                        </div>
                                                                        <div className="text-right">
                                                                            <div className="text-xs font-medium text-gray-600 dark:text-gray-300">
                                                                                {seg.flightNumber}
                                                                            </div>
                                                                            {seg.duration && (
                                                                                <div className="text-xs text-gray-400">
                                                                                    {seg.duration}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Layover indicator */}
                                                                {seg.layoverMinutes && seg.layoverMinutes > 0 && idx < flight.segments.length - 1 && (
                                                                    <div className="flex items-center gap-3 py-1 ml-6">
                                                                        <div className="flex-1 flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2 border border-amber-200 dark:border-amber-800">
                                                                            <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                            </svg>
                                                                            <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                                                                                {t('layover_label')}: {formatLayover(seg.layoverMinutes)}
                                                                                {seg.layoverAirport && ` • ${seg.layoverAirport}`}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                };

                                return (
                                    <>
                                        {renderFlightCard(selectedFlightForModal.outbound, 'outbound')}
                                        {renderFlightCard(selectedFlightForModal.return, 'return')}
                                    </>
                                );
                            })()}

                            {/* Package Includes */}
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
                                <h5 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
                                    {t('package_includes_title')}
                                </h5>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                        <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {t('flights_label')}
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                        <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {t('hotel_label')}
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                        <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {t('transfer_label')}
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                        <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {t('taxes_label')}
                                    </div>
                                </div>
                            </div>

                            {/* Close Button */}
                            <button
                                onClick={() => setShowFlightDetailsModal(false)}
                                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-4 rounded-xl transition-all shadow-lg hover:shadow-xl"
                            >
                                {t('close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* TourDetailsModal - Package Tours only */}
            {isPackageTour && (
                <TourDetailsModal
                    isOpen={showFlightOptionsModal}
                    onClose={() => setShowFlightOptionsModal(false)}
                    offerId={flightOptionsOfferId}
                    searchId={searchQuery?.searchId}
                    currentOutbound={selectedOutboundFlight ? {
                        id: selectedOutboundFlight.id,
                        offerId: flightOptionsOfferId,
                        direction: 'outbound',
                        flightNumber: selectedOutboundFlight.flightNumber,
                        airline: {
                            name: selectedOutboundFlight.airline,
                            code: '',
                            logo: selectedOutboundFlight.logoUrl || ''
                        },
                        departure: {
                            airport: selectedOutboundFlight.departureAirport,
                            code: selectedOutboundFlight.departureAirport,
                            time: selectedOutboundFlight.departureTime,
                            date: selectedOutboundFlight.departureDate
                        },
                        arrival: {
                            airport: selectedOutboundFlight.arrivalAirport,
                            code: selectedOutboundFlight.arrivalAirport,
                            time: selectedOutboundFlight.arrivalTime,
                            date: ''
                        },
                        duration: selectedOutboundFlight.duration,
                        stops: selectedOutboundFlight.stops || 0,
                        priceDifference: selectedOutboundFlight.priceDifference || 0,
                        isDefault: true,
                        isAvailable: true
                    } : undefined}
                    currentInbound={selectedInboundFlight ? {
                        id: selectedInboundFlight.id,
                        offerId: flightOptionsOfferId,
                        direction: 'inbound',
                        flightNumber: selectedInboundFlight.flightNumber,
                        airline: {
                            name: selectedInboundFlight.airline,
                            code: '',
                            logo: selectedInboundFlight.logoUrl || ''
                        },
                        departure: {
                            airport: selectedInboundFlight.departureAirport,
                            code: selectedInboundFlight.departureAirport,
                            time: selectedInboundFlight.departureTime,
                            date: selectedInboundFlight.departureDate
                        },
                        arrival: {
                            airport: selectedInboundFlight.arrivalAirport,
                            code: selectedInboundFlight.arrivalAirport,
                            time: selectedInboundFlight.arrivalTime,
                            date: ''
                        },
                        duration: selectedInboundFlight.duration,
                        stops: selectedInboundFlight.stops || 0,
                        priceDifference: selectedInboundFlight.priceDifference || 0,
                        isDefault: true,
                        isAvailable: true
                    } : undefined}
                    onSelectFlights={(outbound, inbound) => {
                        console.log('✅ Flight selection confirmed:', {
                            outbound: outbound ? { id: outbound.id, offerId: outbound.offerId, priceDiff: outbound.priceDifference } : null,
                            inbound: inbound ? { id: inbound.id, offerId: inbound.offerId, priceDiff: inbound.priceDifference } : null,
                        });
                        if (outbound) {
                            setSelectedOutboundFlight({
                                id: outbound.id,
                                offerId: outbound.offerId || '',
                                flightNumber: outbound.flightNumber,
                                airline: outbound.airline.name,
                                departureAirport: outbound.departure.code,
                                arrivalAirport: outbound.arrival.code,
                                departureTime: outbound.departure.time,
                                arrivalTime: outbound.arrival.time,
                                departureDate: outbound.departure.date,
                                duration: outbound.duration,
                                stops: outbound.stops ?? 0,
                                logoUrl: outbound.airline.logo,
                                priceDifference: outbound.priceDifference,
                                brandedFareId: outbound.brandedFareId
                            });
                        }
                        if (inbound) {
                            setSelectedInboundFlight({
                                id: inbound.id,
                                offerId: inbound.offerId || '',
                                flightNumber: inbound.flightNumber,
                                airline: inbound.airline.name,
                                departureAirport: inbound.departure.code,
                                arrivalAirport: inbound.arrival.code,
                                departureTime: inbound.departure.time,
                                arrivalTime: inbound.arrival.time,
                                departureDate: inbound.departure.date,
                                duration: inbound.duration,
                                stops: inbound.stops ?? 0,
                                logoUrl: inbound.airline.logo,
                                priceDifference: inbound.priceDifference,
                                brandedFareId: inbound.brandedFareId
                            });
                        }
                    }}
                    basePrice={lowestPrice}
                    currency={lowestCurrency}
                />
            )}
        </div>

    );
};
